// Branch-aware opening courses. Content is bilingual data; the UI chrome stays
// in the i18n catalog. Authored lines are flattened for readable diffs, then
// merged into a move trie so either side can train against all variations.

import type { Color } from "../game/types.ts";
import { SUPPORTED_OPENING_COURSES } from "./supportedOpeningCourses.ts";
import { chooseRanked } from "../game/rankedChoice.ts";

export interface L10n {
  en: string;
  sl: string;
}

/** Catalog sections follow the classical first-move families, so the section
 *  itself already tells the learner how each opening starts. */
export type CourseGroup = "open" | "semi-open" | "closed-flank";
export type CourseKind = "guided" | "supported";
export type MoveRole = "primary" | "alternative" | "retry";
export type MoveVerdict = "best" | "good" | "mistake";

export interface CourseShape {
  orig: string;
  dest?: string;
  brush: "green" | "red" | "paleBlue" | "paleGreen" | "paleGrey";
}

export interface MoveSpec {
  uci: string;
  san: string;
  explain?: L10n;
  hint?: L10n;
  shapes?: CourseShape[];
  /** An instructive terminal mistake: explain it, take it back, and retry. */
  reject?: L10n;
}

export interface CourseLine {
  id: string;
  /** Line to share a prefix with; defaults to the first (main) line. */
  base?: string;
  /** Number of plies shared with `base` before this line diverges. */
  at?: number;
  /** Variation name, announced on the first diverging move. */
  name?: L10n;
  /** Moves after the shared prefix, or the complete main line. */
  moves: MoveSpec[];
}

export interface OpeningCourse {
  slug: string;
  eco: string;
  /** Guided courses have authored coaching; supported courses are names-first. */
  kind?: CourseKind;
  name: L10n;
  tagline: L10n;
  group: CourseGroup;
  /** Default side; the same tree can be trained from either side. */
  color: Color;
  intro: L10n;
  lines: CourseLine[];
  summary: L10n;
}

export interface ResolvedCourseLine {
  id: string;
  moves: MoveSpec[];
  source: CourseLine;
}

export interface CourseNode extends MoveSpec {
  /** Name of the variation entered by this move, if it is a branch point. */
  name?: L10n;
  children: CourseNode[];
  /** Stable progress key for the path from the initial position through here. */
  pathKey: string;
}

/** Resolve `base`/`at` references into complete root-to-line move arrays. */
export function resolveCourseLines(course: OpeningCourse): ResolvedCourseLine[] {
  if (course.lines.length === 0) throw new Error(`${course.slug}: course has no lines`);
  const byId = new Map<string, CourseLine>();
  for (const line of course.lines) {
    if (!line.id || byId.has(line.id)) throw new Error(`${course.slug}: duplicate/empty line id ${line.id}`);
    byId.set(line.id, line);
  }
  const cache = new Map<string, MoveSpec[]>();
  const resolving = new Set<string>();
  const mainId = course.lines[0].id;

  const resolve = (line: CourseLine): MoveSpec[] => {
    const hit = cache.get(line.id);
    if (hit) return hit;
    if (resolving.has(line.id)) throw new Error(`${course.slug}: cyclic base at ${line.id}`);
    resolving.add(line.id);

    let moves: MoveSpec[];
    if (line.id === mainId && line.base === undefined && line.at === undefined) {
      moves = [...line.moves];
    } else {
      const baseId = line.base ?? mainId;
      const base = byId.get(baseId);
      if (!base || base === line) throw new Error(`${course.slug}/${line.id}: unknown or self base ${baseId}`);
      if (!Number.isInteger(line.at)) throw new Error(`${course.slug}/${line.id}: branch needs integer at`);
      const baseMoves = resolve(base);
      const at = line.at!;
      if (at < 0 || at > baseMoves.length) throw new Error(`${course.slug}/${line.id}: at ${at} out of range`);
      if (line.moves.length === 0) throw new Error(`${course.slug}/${line.id}: empty branch`);
      if (baseMoves[at]?.uci === line.moves[0].uci) {
        throw new Error(`${course.slug}/${line.id}: first move does not diverge at ${at}`);
      }
      moves = [...baseMoves.slice(0, at), ...line.moves];
    }
    resolving.delete(line.id);
    cache.set(line.id, moves);
    return moves;
  };

  return course.lines.map((source) => ({ id: source.id, moves: resolve(source), source }));
}

const treeCache = new WeakMap<OpeningCourse, CourseNode[]>();

/** Merge all authored lines into a trie. Child order preserves line priority. */
export function buildCourseTree(course: OpeningCourse): CourseNode[] {
  const cached = treeCache.get(course);
  if (cached) return cached;
  const roots: CourseNode[] = [];
  for (const line of resolveCourseLines(course)) {
    let children = roots;
    let path = "";
    const branchAt = line.source.at ?? 0;
    line.moves.forEach((move, ply) => {
      path = path ? `${path} ${move.uci}` : move.uci;
      let node = children.find((child) => child.uci === move.uci);
      if (!node) {
        node = { ...move, children: [], pathKey: path };
        children.push(node);
      } else {
        node.explain ??= move.explain;
        node.hint ??= move.hint;
        node.shapes ??= move.shapes;
        node.reject ??= move.reject;
      }
      if (line.source.name && ply === branchAt) node.name ??= line.source.name;
      children = node.children;
    });
  }
  treeCache.set(course, roots);
  return roots;
}

/** Numbered SAN of the course's main line, e.g. "1. e4 e5 2. Nf3 Nc6 3. Bc4".
 *  Courses always start from the initial position, so ply 0 is White's move 1.
 *  Truncation past `maxPlies` is marked with an ellipsis. */
export function courseMainline(course: OpeningCourse, maxPlies = 6): string {
  const main = resolveCourseLines(course)[0].moves;
  const parts: string[] = [];
  for (const [ply, move] of main.slice(0, maxPlies).entries()) {
    parts.push(ply % 2 === 0 ? `${ply / 2 + 1}. ${move.san}` : move.san);
  }
  return main.length > maxPlies ? `${parts.join(" ")} …` : parts.join(" ");
}

/** Accepted (non-retry) leaves used for line-coverage progress. */
export function courseLeafPaths(course: OpeningCourse): string[] {
  const out: string[] = [];
  const walk = (node: CourseNode): void => {
    if (node.reject) return;
    const accepted = node.children.filter((child) => !child.reject);
    if (accepted.length === 0) out.push(node.pathKey);
    else accepted.forEach(walk);
  };
  buildCourseTree(course).filter((node) => !node.reject).forEach(walk);
  return out;
}

/** Choose one uncovered leaf for a run; once complete, revisit any leaf. */
export function selectCourseTarget(
  leafPaths: readonly string[],
  completed: ReadonlySet<string>,
  rng: () => number = Math.random,
): string | undefined {
  const uncovered = leafPaths.filter((path) => !completed.has(path));
  const candidates = uncovered.length ? uncovered : [...leafPaths];
  return chooseRanked(candidates, rng);
}

/**
 * Next-branch navigation: walk leaves in catalog order (0 = main, 1 = next by
 * author rank / popularity, …) and wrap. Prefer the next uncovered leaf after
 * `currentPath`; when every leaf is covered, advance one step in the circle.
 * Returns undefined when there is nothing else to switch to (0 or 1 leaves, or
 * only the current leaf exists).
 */
export function selectNextBranchTarget(
  leafPaths: readonly string[],
  completed: ReadonlySet<string>,
  currentPath?: string,
): string | undefined {
  if (leafPaths.length < 2) return undefined;
  const currentIndex = currentPath ? leafPaths.indexOf(currentPath) : -1;
  const start = currentIndex >= 0 ? currentIndex + 1 : 0;
  const uncovered = leafPaths.filter((path) => !completed.has(path));
  if (uncovered.length > 0) {
    for (let offset = 0; offset < leafPaths.length; offset++) {
      const path = leafPaths[(start + offset) % leafPaths.length];
      if (!completed.has(path) && path !== currentPath) return path;
    }
    return uncovered.find((path) => path !== currentPath) ?? uncovered[0];
  }
  // All covered: strict cycle 0 → 1 → 2 → … → 0.
  return leafPaths[start % leafPaths.length];
}

/** Shared root plies between two course paths. */
export function commonPrefixLength(a: readonly string[], b: readonly string[]): number {
  let length = 0;
  while (length < a.length && length < b.length && a[length] === b[length]) length++;
  return length;
}

/** Number of already-played plies to keep when switching to `targetPath`. */
export function rewindToFork(played: readonly string[], targetPath: string): number {
  return commonPrefixLength(played, targetPath.split(" ").filter(Boolean));
}

const GUIDED_BOOK_FAMILIES: Readonly<Record<string, readonly string[]>> = {
  italian: ["Italian Game"],
  "ruy-lopez": ["Ruy Lopez", "Ruy López"],
  "queens-gambit": ["Queen's Gambit", "Queen's Gambit Declined", "Queen's Gambit Accepted"],
  "caro-kann": ["Caro-Kann Defense"],
  sicilian: ["Sicilian Defense"],
  london: ["London System"],
  french: ["French Defense"],
  slav: ["Slav Defense"],
  scandinavian: ["Scandinavian Defense"],
  catalan: ["Catalan Opening"],
  "nimzo-indian": ["Nimzo-Indian Defense"],
  "reti-english": ["Réti Opening", "English Opening"],
  "modern-benoni": ["Benoni Defense"],
};

function normalizedOpeningName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/^the\s+/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Resolve a canonical book name to one of the deliberately supported
 * courses. A mere name in the full ECO corpus is not enough: the complete
 * family or variation name must occur in our catalogued allowlist. */
export function findCourseForBookOpening(
  opening: { eco: string; name: string } | undefined,
): OpeningCourse | undefined {
  if (!opening) return undefined;
  const family = opening.name.split(":", 1)[0];
  const normalizedFamily = normalizedOpeningName(family);
  const normalizedFullName = normalizedOpeningName(opening.name);
  return OPENING_COURSES.find((course) => {
    const familyNames = [course.name.en, ...(GUIDED_BOOK_FAMILIES[course.slug] ?? [])];
    const familyMatches = familyNames.some((name) => {
      const normalized = normalizedOpeningName(name);
      return normalizedFamily === normalized || normalizedFamily.startsWith(`${normalized} `);
    });
    if (!familyMatches) return false;
    const approvedNames = [
      ...familyNames,
      ...course.lines.flatMap((line) => line.name?.en ? [line.name.en] : []),
    ];
    return approvedNames.some((name) => normalizedOpeningName(name) === normalizedFullName);
  });
}

/** The authored child belonging to the run's current target leaf. */
export function targetCourseChoice(
  choices: readonly CourseNode[],
  targetPath: string | undefined,
  ply: number,
): CourseNode | undefined {
  const uci = targetPath?.split(" ")[ply];
  return choices.find((choice) => !choice.reject && choice.uci === uci) ?? choices.find((choice) => !choice.reject);
}

/** Stable learner-facing quality labels for reviewed course branches. */
export function assessCourseMove(node: CourseNode, siblings: readonly CourseNode[]): {
  role: MoveRole;
  verdict: MoveVerdict;
  index: number;
  total: number;
} {
  if (node.reject) return { role: "retry", verdict: "mistake", index: 0, total: 0 };
  const accepted = siblings.filter((choice) => !choice.reject);
  const index = Math.max(0, accepted.indexOf(node));
  return {
    role: index === 0 ? "primary" : "alternative",
    verdict: index === 0 ? "best" : "good",
    index: index + 1,
    total: accepted.length,
  };
}

export const OPENING_COURSES: OpeningCourse[] = [
  {
    slug: "italian",
    group: "open",
    eco: "C50",
    name: { en: "The Italian Game", sl: "Italijanska otvoritev" },
    tagline: { en: "The classical attacking school", sl: "Klasična šola napada" },
    color: "white",
    intro: {
      en: "The oldest opening in the book and still a top-level weapon: rapid development, a bishop aimed at f7, and a flexible pawn center.",
      sl: "Najstarejša otvoritev v knjigah in še vedno orožje vrhunskih igralcev: hiter razvoj, lovec, uperjen v f7, in prožno kmečko središče.",
    },
    summary: {
      en: "The Italian recipe: fast development (knight f3, bishop c4), a modest center (c3+d3) ready to expand with d4, castle early — then attack f7 or break in the middle. Play it against the computer to make it stick.",
      sl: "Italijanski recept: hiter razvoj (skakač f3, lovec c4), skromno središče (c3+d3), pripravljeno na širitev z d4, zgodnja rokada — nato napad na f7 ali prodor v sredini. Utrdi znanje s partijo proti računalniku.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "e2e4",
            san: "e4",
            hint: { en: "Take the center with a pawn.", sl: "Zasedi središče s kmetom." },
            explain: {
              en: "Claim the center and open lines for the queen and the light-squared bishop. The fight for the middle starts on move one.",
              sl: "Zasedi središče in odpri poti dami ter belopoljnemu lovcu. Boj za sredino se začne s prvo potezo.",
            },
          },
          {
            uci: "e7e5",
            san: "e5",
            explain: {
              en: "Black stakes an equal claim. Both sides now fight for the central squares.",
              sl: "Črni enakovredno zasede središče. Oba se zdaj borita za osrednja polja.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            hint: {
              en: "Develop a knight and attack the e5 pawn at the same time.",
              sl: "Razvij skakača in hkrati napadi kmeta na e5.",
            },
            explain: {
              en: "Develop with a threat: the knight attacks e5, so Black must react — you gain time.",
              sl: "Razvij se z grožnjo: skakač napade kmeta na e5, črni mora odgovoriti — pridobiš tempo.",
            },
          },
          {
            uci: "b8c6",
            san: "Nc6",
            explain: {
              en: "Black defends e5 with a developing move — the most natural reply.",
              sl: "Črni brani e5 z razvojno potezo — najbolj naraven odgovor.",
            },
          },
          {
            uci: "f1c4",
            san: "Bc4",
            hint: {
              en: "Aim your bishop at Black's weakest point.",
              sl: "Usmeri lovca na najšibkejšo črno točko.",
            },
            shapes: [{"orig":"c4","dest":"f7","brush":"red"}],
            explain: {
              en: "The Italian bishop eyes f7 — the weakest square in Black's camp, defended only by the king.",
              sl: "Italijanski lovec meri na f7 — najšibkejše polje v črnem taboru, ki ga brani samo kralj.",
            },
          },
          {
            uci: "f8c5",
            san: "Bc5",
            explain: {
              en: "Black mirrors: the bishop takes the strong a7–g1 diagonal, aiming at f2.",
              sl: "Črni zrcali: lovec zasede močno diagonalo a7–g1 in meri na f2.",
            },
          },
          {
            uci: "c2c3",
            san: "c3",
            hint: { en: "Prepare to push d4 next.", sl: "Pripravi potisk d4." },
            explain: {
              en: "Quiet but poisonous: the pawn prepares d4 and builds a big center. Note it takes the c3 square from the b1 knight — that knight will go via d2 instead.",
              sl: "Tiha, a strupena poteza: kmet pripravlja d4 in gradi široko središče. Skakaču z b1 sicer vzame polje c3 — ta bo šel prek d2.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            explain: {
              en: "Black develops and counterattacks e4 — the critical test of your setup.",
              sl: "Črni se razvija in protinapada e4 — kritična preizkušnja tvoje postavitve.",
            },
          },
          {
            uci: "d2d3",
            san: "d3",
            hint: {
              en: "Your e4 pawn is attacked — protect it.",
              sl: "Tvoj kmet na e4 je napaden — zaščiti ga.",
            },
            explain: {
              en: "Defend e4 first. Patience: the d4 push stays in reserve, you castle quickly, and only then start the fight.",
              sl: "Najprej zavaruj e4. Potrpljenje: potisk d4 ostaja v rezervi, hitro rokiraš in šele nato začneš boj.",
            },
          },
          {
            uci: "d7d6",
            san: "d6",
            explain: {
              en: "Black shores up e5 and opens the c8 bishop. The position is symmetrical — the better-prepared player wins the middlegame.",
              sl: "Črni podpre e5 in odpre lovca na c8. Položaj je simetričen — srednjo igro dobi bolje pripravljeni.",
            },
          },
          {
            uci: "e1g1",
            san: "O-O",
            hint: { en: "Bring your king to safety.", sl: "Spravi kralja na varno." },
            explain: {
              en: "King safety before glory: castling connects the rooks and tucks the king away. Now you're ready for d4 or the classic kingside build-up.",
              sl: "Varnost kralja pred slavo: rokada poveže trdnjavi in skrije kralja. Zdaj si pripravljen na d4 ali klasično gradnjo na kraljevem krilu.",
            },
          },
        ],
      },
      {
        id: "no-d3-6",
        at: 6,
        moves: [{ uci: "d2d3", san: "d3", reject: {
            en: "The 'Quiet Italian' with d3 is also perfectly sound — but in this course we build the big center with c3.",
            sl: "'Tiha italijanka' z d3 je prav tako povsem zdrava — a v tem tečaju gradimo široko središče s c3.",
          } }],
      },
    ],
  },
  {
    slug: "ruy-lopez",
    group: "open",
    eco: "C88",
    name: { en: "The Ruy Lopez", sl: "Španska otvoritev" },
    tagline: {
      en: "Pressure chess, played by every world champion",
      sl: "Šah pritiska, ki ga je igral vsak svetovni prvak",
    },
    color: "white",
    intro: {
      en: "The Spanish squeeze: instead of attacking f7, White pesters the knight that defends e5 — and keeps the pressure for twenty moves. These eight moves are the most-played opening sequence in chess history.",
      sl: "Španski primež: namesto napada na f7 beli nadleguje skakača, ki brani e5 — in pritisk vzdržuje dvajset potez. Teh osem potez je najpogosteje igrano otvoritveno zaporedje v zgodovini šaha.",
    },
    summary: {
      en: "The Ruy Lopez is pressure chess: pin and pester the defender of e5, castle fast, use e-file tactics instead of defending pawns, then build the big center with c3 and d4. You now know the eight moves every world champion has played.",
      sl: "Španka je šah pritiska: veži in nadleguj branilca polja e5, hitro rokiraj, namesto branjenja kmetov uporabljaj taktiko po e-liniji, nato s c3 in d4 zgradi široko središče. Zdaj poznaš osem potez, ki jih je igral vsak svetovni prvak.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "e2e4",
            san: "e4",
            hint: { en: "Take the center.", sl: "Zasedi središče." },
            explain: {
              en: "Center and open lines — the classical start.",
              sl: "Središče in odprte linije — klasičen začetek.",
            },
          },
          {
            uci: "e7e5",
            san: "e5",
            explain: { en: "Black answers symmetrically.", sl: "Črni odgovori simetrično." },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            hint: { en: "Develop with an attack on e5.", sl: "Razvij se z napadom na e5." },
            explain: { en: "The knight develops and attacks e5.", sl: "Skakač se razvije in napade e5." },
          },
          {
            uci: "b8c6",
            san: "Nc6",
            explain: { en: "Defended — for now.", sl: "Ubranjen — za zdaj." },
          },
          {
            uci: "f1b5",
            san: "Bb5",
            hint: {
              en: "Instead of aiming at f7, pressure the defender of e5.",
              sl: "Namesto na f7 pritisni na branilca polja e5.",
            },
            shapes: [{"orig":"b5","dest":"c6","brush":"paleBlue"},{"orig":"c6","dest":"e5","brush":"paleGreen"}],
            explain: {
              en: "The Spanish move: attack the knight that defends e5. Indirect pressure on the center that can last the whole game.",
              sl: "Španska poteza: napadi skakača, ki brani e5. Posreden pritisk na središče, ki lahko traja vso partijo.",
            },
          },
          {
            uci: "a7a6",
            san: "a6",
            explain: {
              en: "The Morphy Defense — Black puts the question to the bishop: capture or retreat?",
              sl: "Morphyjeva obramba — črni vpraša lovca: vzameš ali se umakneš?",
            },
          },
          {
            uci: "b5a4",
            san: "Ba4",
            hint: { en: "Retreat, but keep eyeing c6.", sl: "Umakni se, a še naprej glej proti c6." },
            explain: {
              en: "Keep the tension. Taking on c6 followed by grabbing e5 wins nothing: Black's queen comes to d4, forking, and regains the pawn. The bishop stays on the a4–e8 diagonal.",
              sl: "Ohrani napetost. Jemanje na c6 in nato na e5 ne prinese ničesar: črna dama pride na d4, z vilico, in kmeta dobi nazaj. Lovec ostane na diagonali a4–e8.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            explain: {
              en: "Black develops and hits e4 — but White doesn't need to defend it…",
              sl: "Črni razvija in napade e4 — a belemu ga ni treba braniti…",
            },
          },
          {
            uci: "e1g1",
            san: "O-O",
            hint: {
              en: "e4 looks attacked… but is it really? Castle.",
              sl: "Videti je, da je e4 napaden… a je res? Rokiraj.",
            },
            explain: {
              en: "The famous point: e4 only *appears* to hang. If Black grabs it, the rook comes to e1 and White wins the pawn back with a better game. Castle, and let tactics do the defending.",
              sl: "Znamenita poanta: e4 samo *na videz* visi. Če ga črni vzame, pride trdnjava na e1 in beli kmeta dobi nazaj z boljšo igro. Rokiraj in naj branijo taktike.",
            },
          },
          {
            uci: "f8e7",
            san: "Be7",
            explain: {
              en: "Black plays it safe, blocking the e-file before grabbing anything.",
              sl: "Črni igra varno in zapre e-linijo, preden bi kaj vzel.",
            },
          },
          {
            uci: "f1e1",
            san: "Re1",
            hint: { en: "Defend e4 with a developing move.", sl: "Brani e4 z razvojno potezo." },
            explain: {
              en: "Now e4 is truly defended — and the rook stares down the e-file. Black must again mind the capture on c6 followed by the fall of e5.",
              sl: "Zdaj je e4 zares branjen — trdnjava gleda po e-liniji. Črni mora spet paziti na jemanje na c6 in nato padec kmeta e5.",
            },
          },
          {
            uci: "b7b5",
            san: "b5",
            explain: {
              en: "Sooner or later forced: Black breaks the pressure on c6 by hitting the bishop.",
              sl: "Prej ali slej prisiljeno: črni z napadom na lovca prekine pritisk na c6.",
            },
          },
          {
            uci: "a4b3",
            san: "Bb3",
            hint: {
              en: "Only one square keeps the bishop active.",
              sl: "Le eno polje ohrani lovca aktivnega.",
            },
            explain: {
              en: "The bishop lands on its dream diagonal a2–g8, once again eyeing f7 — a full tour, and not a single piece traded.",
              sl: "Lovec pristane na sanjski diagonali a2–g8 in spet meri na f7 — cel obhod, pa še vedno nobene menjave.",
            },
          },
          {
            uci: "d7d6",
            san: "d6",
            explain: {
              en: "Black solidifies e5 and prepares to finish development.",
              sl: "Črni utrdi e5 in pripravi dokončanje razvoja.",
            },
          },
          {
            uci: "c2c3",
            san: "c3",
            hint: {
              en: "Prepare d4 — and give your bishop an escape square.",
              sl: "Pripravi d4 — in lovcu zagotovi polje za umik.",
            },
            explain: {
              en: "Two ideas in one: prepare the d4 break, and give the bishop a retreat on c2 when Black's knight jumps to a5.",
              sl: "Dve ideji v eni: priprava prodora d4 in umik za lovca na c2, ko črni skakač skoči na a5.",
            },
          },
          {
            uci: "e8g8",
            san: "O-O",
            explain: {
              en: "Both kings safe — the classic closed Ruy Lopez position. Here the real battle begins: White continues h3, d4 and the famous knight tour b1–d2–f1–g3.",
              sl: "Oba kralja na varnem — klasični zaprti španski položaj. Tu se začne pravi boj: beli nadaljuje s h3, d4 in znamenitim obhodom skakača b1–d2–f1–g3.",
            },
          },
        ],
      },

    ],
  },
  {
    slug: "queens-gambit",
    group: "closed-flank",
    eco: "D53",
    name: { en: "The Queen's Gambit Declined", sl: "Zavrnjeni damin gambit" },
    tagline: { en: "Positional chess in its purest form", sl: "Pozicijski šah v najčistejši obliki" },
    color: "white",
    intro: {
      en: "White's other great first move. The 'gambit' is an illusion — White always regains the pawn — but the pressure on d5 is real. This is the opening of Capablanca, Karpov and countless world-title matches.",
      sl: "Druga velika prva poteza belega. 'Gambit' je le navidezen — beli kmeta vedno dobi nazaj — a pritisk na d5 je resničen. To je otvoritev Capablance, Karpova in neštetih dvobojev za naslov prvaka.",
    },
    summary: {
      en: "QGD play revolves around the d5 strong point: attack it with pieces (c4, knight c3, bishop g5), develop modestly (e3, knight f3, bishop d3), castle, then act on the c-file. And remember Black's buried c8 bishop: trade the other pieces off, and that bishop loses the endgame for Black.",
      sl: "Igra zavrnjenega daminega gambita se vrti okoli oporne točke d5: napadaj jo s figurami (c4, skakač c3, lovec g5), razvijaj se skromno (e3, skakač f3, lovec d3), rokiraj in nato deluj po c-liniji. In ne pozabi na zakopanega črnega lovca c8: zamenjaj ostale figure in ta lovec črnemu izgubi končnico.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "d2d4",
            san: "d4",
            hint: {
              en: "Take the center — with the pawn that's already protected.",
              sl: "Zasedi središče — s kmetom, ki je že zavarovan.",
            },
            explain: {
              en: "The queen's pawn takes the center, protected from move one (unlike e4). Positional chess starts here.",
              sl: "Damin kmet zasede središče in je za razliko od e4 zavarovan že od prve poteze. Tu se začne pozicijski šah.",
            },
          },
          {
            uci: "d7d5",
            san: "d5",
            explain: { en: "Black matches it.", sl: "Črni odgovori enako." },
          },
          {
            uci: "c2c4",
            san: "c4",
            hint: { en: "Offer a pawn to undermine d5.", sl: "Ponudi kmeta, da spodkoplješ d5." },
            explain: {
              en: "The Queen's Gambit: offer a wing pawn to lure Black's d-pawn away from the center. It's no real sacrifice — White always gets it back.",
              sl: "Damin gambit: ponudi krilnega kmeta, da zvabiš črnega d-kmeta iz središča. Ni prava žrtev — beli ga vedno dobi nazaj.",
            },
          },
          {
            uci: "e7e6",
            san: "e6",
            explain: {
              en: "Declined: Black keeps the strong point d5 — at the price of locking in the c8 bishop, the eternal problem piece of this opening.",
              sl: "Gambit zavrnjen: črni obdrži oporno točko d5 — za ceno zaprtega lovca na c8, večne problemske figure te otvoritve.",
            },
          },
          {
            uci: "b1c3",
            san: "Nc3",
            hint: {
              en: "Develop a knight and pressure d5 again.",
              sl: "Razvij skakača in znova pritisni na d5.",
            },
            explain: {
              en: "Develop toward the center and add a second attacker on d5.",
              sl: "Razvij se proti središču in dodaj drugega napadalca na d5.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            explain: { en: "Black defends d5 and develops.", sl: "Črni brani d5 in se razvija." },
          },
          {
            uci: "c1g5",
            san: "Bg5",
            hint: { en: "Attack the knight that protects d5.", sl: "Napadi skakača, ki brani d5." },
            shapes: [{"orig":"g5","dest":"f6","brush":"paleBlue"},{"orig":"f6","dest":"d5","brush":"paleGreen"}],
            explain: {
              en: "Pin the defender! The bishop ties the f6 knight to the queen — fighting for d5 with pieces, not just pawns.",
              sl: "Veži branilca! Lovec priklene skakača f6 na damo — za d5 se ne borijo le kmetje, ampak figure.",
            },
          },
          {
            uci: "f8e7",
            san: "Be7",
            explain: { en: "Black calmly breaks the pin.", sl: "Črni mirno prekine vez." },
          },
          {
            uci: "e2e3",
            san: "e3",
            hint: { en: "Open a path for your other bishop.", sl: "Odpri pot še drugemu lovcu." },
            explain: {
              en: "Modest but right: open the f1 bishop, support d4, keep the pawn chain flexible. Note the g5 bishop got out *before* this door closed.",
              sl: "Skromno, a pravilno: odpri lovca f1, podpri d4 in ohrani gibko kmečko verigo. Lovec z g5 je šel ven, *preden* so se ta vrata zaprla.",
            },
          },
          {
            uci: "e8g8",
            san: "O-O",
            explain: {
              en: "Black castles — the safest square on the board.",
              sl: "Črni rokira — najvarnejše polje na plošči.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            hint: {
              en: "One more developing move before castling.",
              sl: "Še ena razvojna poteza pred rokado.",
            },
            explain: {
              en: "Complete the picture: knight to its best square, castling next. This is the classical QGD — Capablanca's bread and butter.",
              sl: "Dopolni sliko: skakač na najboljše polje, sledi rokada. To je klasični zavrnjeni damin gambit — Capablancov vsakdanji kruh.",
            },
          },
          {
            uci: "b8d7",
            san: "Nbd7",
            explain: {
              en: "Black's knights take their classic posts. White follows with the rook to c1 and the bishop to d3 — a plan over a century old that still wins games.",
              sl: "Črna skakača zasedeta klasični mesti. Beli nadaljuje s trdnjavo na c1 in lovcem na d3 — načrt, star več kot sto let, ki še vedno zmaguje.",
            },
          },
        ],
      },

    ],
  },
  {
    slug: "caro-kann",
    group: "semi-open",
    eco: "B18",
    name: { en: "The Caro-Kann", sl: "Caro-Kann" },
    tagline: { en: "The rock-solid answer to 1.e4", sl: "Skalno trden odgovor na 1.e4" },
    color: "black",
    intro: {
      en: "Play with Black! The Caro-Kann strikes at the center while keeping a clean, weakness-free structure — and unlike the French, it frees the light-squared bishop first. A favorite of Capablanca, Karpov and today's elite.",
      sl: "Igraj s črnimi! Caro-Kann udari po središču in hkrati ohrani čisto strukturo brez šibkosti — in za razliko od francoske najprej osvobodi belopoljnega lovca. Ljubljenec Capablance, Karpova in današnje elite.",
    },
    summary: {
      en: "The Caro-Kann creed: strike the center with …d5, free the c8 bishop *before* playing …e6, never accept weaknesses, and let White overextend. You get the same solid wall every game — and endgames you win on structure alone.",
      sl: "Credo Caro-Kanna: udari po središču z …d5, osvobodi lovca c8, *preden* igraš …e6, ne sprejemaj šibkosti in pusti belemu, da se prenapne. V vsaki partiji dobiš enako trden zid — in končnice, ki jih dobivaš zgolj na boljši strukturi.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "e2e4",
            san: "e4",
            explain: {
              en: "White grabs the center. The Caro-Kann answers with a plan, not a mirror.",
              sl: "Beli zasede središče. Caro-Kann ne odgovori z zrcalom, ampak z načrtom.",
            },
          },
          {
            uci: "c7c6",
            san: "c6",
            hint: {
              en: "Prepare the …d5 strike without blocking your c8 bishop.",
              sl: "Pripravi udar …d5, ne da bi zaprl pot lovcu c8.",
            },
            explain: {
              en: "This modest pawn supports tomorrow's …d5 strike. Unlike the French (…e6), it keeps the c8 bishop's diagonal open — the whole point of the opening.",
              sl: "Skromni kmet podpre jutrišnji udar …d5. Za razliko od francoske (…e6) pušča diagonalo lovca c8 odprto — v tem je vsa poanta otvoritve.",
            },
          },
          {
            uci: "d2d4",
            san: "d4",
            explain: {
              en: "White builds the ideal center — for now.",
              sl: "Beli postavi idealno središče — za zdaj.",
            },
          },
          {
            uci: "d7d5",
            san: "d5",
            hint: { en: "Strike the center, as planned.", sl: "Po načrtu udari po središču." },
            explain: {
              en: "Challenge e4 immediately. White must resolve the central tension.",
              sl: "Takoj izzovi e4. Beli mora razrešiti napetost v središču.",
            },
          },
          {
            uci: "b1c3",
            san: "Nc3",
            explain: {
              en: "The main line: White defends e4 with a piece.",
              sl: "Glavna varianta: beli brani e4 s figuro.",
            },
          },
          {
            uci: "d5e4",
            san: "dxe4",
            hint: { en: "Release the tension — capture!", sl: "Sprosti napetost — vzemi!" },
            explain: {
              en: "Trade the d-pawn for the e-pawn: Black gets a rock-solid, weakness-free structure. The small price: White is a little freer for a while.",
              sl: "Zamenjaj d-kmeta za e-kmeta: črni dobi skalno trdno strukturo brez šibkosti. Majhna cena: beli ima nekaj časa malo več prostora.",
            },
          },
          {
            uci: "c3e4",
            san: "Nxe4",
            explain: {
              en: "The knight recaptures and stands proudly in the center — but nothing holds it there.",
              sl: "Skakač vzame nazaj in ponosno stoji sredi plošče — a nič ga tam ne drži.",
            },
          },
          {
            uci: "c8f5",
            san: "Bf5",
            hint: {
              en: "Develop the bishop with tempo — before …e6 shuts it in.",
              sl: "Razvij lovca s tempom — preden ga …e6 zapre.",
            },
            shapes: [{"orig":"f5","dest":"e4","brush":"paleBlue"}],
            explain: {
              en: "The star move: the 'problem bishop' reaches its dream post AND attacks the knight. This is exactly what 1…c6 made possible.",
              sl: "Zvezdniška poteza: 'problemski lovec' pride na sanjsko polje IN napade skakača. Prav to je omogočila poteza 1…c6.",
            },
          },
          {
            uci: "e4g3",
            san: "Ng3",
            explain: {
              en: "The knight retreats and hits the bishop — White hopes to win time.",
              sl: "Skakač se umakne in napade lovca — beli upa na pridobljen čas.",
            },
          },
          {
            uci: "f5g6",
            san: "Bg6",
            hint: {
              en: "Keep the bishop on its diagonal, out of the knight's reach.",
              sl: "Ohrani lovca na diagonali, zunaj dosega skakača.",
            },
            explain: {
              en: "Sidestep to safety while staying on the b1–h7 diagonal. Now comes White's most aggressive try…",
              sl: "Umik na varno, a še vedno na diagonali b1–h7. Zdaj pride najostrejši beli poskus…",
            },
          },
          {
            uci: "h2h4",
            san: "h4",
            explain: {
              en: "White lunges: the h-pawn threatens to trap the bishop with h5. Dangerous — but the Caro-Kann has a precise answer.",
              sl: "Beli zamahne: kmet h grozi, da s h5 ujame lovca. Nevarno — a Caro-Kann ima natančen odgovor.",
            },
          },
          {
            uci: "h7h6",
            san: "h6",
            hint: {
              en: "Calmly give the bishop an escape square.",
              sl: "Mirno zagotovi lovcu polje za umik.",
            },
            explain: {
              en: "Cold-blooded: h7 is now free for the bishop, and …h6 covers g5 forever. White's h4 will later prove a weakness, not an attack.",
              sl: "Hladnokrvno: h7 je zdaj prosto za lovca, …h6 pa za vedno pokrije g5. Beli potisk h4 se bo pozneje izkazal za šibkost, ne za napad.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            explain: {
              en: "White finally develops the kingside.",
              sl: "Beli končno razvija kraljevo krilo.",
            },
          },
          {
            uci: "b8d7",
            san: "Nd7",
            hint: {
              en: "Develop — and quietly prepare …Ngf6 without getting doubled pawns.",
              sl: "Razvij se — in tiho pripravi skakača na f6 brez podvojenih kmetov.",
            },
            explain: {
              en: "The subtle point: this knight prepares …Ngf6 so that after a capture on f6 Black recaptures with the knight, keeping the pawn wall clean. Structure first — that's the Caro-Kann soul.",
              sl: "Prefinjena poanta: ta skakač pripravlja drugega na f6, da bo po menjavi tam lahko vzel nazaj s skakačem in ohranil čisto kmečko verigo. Najprej struktura — to je duša Caro-Kanna.",
            },
          },
        ],
      },

    ],
  },
  {
    slug: "sicilian",
    group: "semi-open",
    eco: "B92",
    name: { en: "The Sicilian Defense", sl: "Sicilijanska obramba" },
    tagline: { en: "Fight for the win with Black", sl: "Boj za zmago s črnimi" },
    color: "black",
    intro: {
      en: "The most popular — and most combative — answer to 1.e4: instead of mirroring, Black trades a wing pawn for a center pawn and gets dynamic, unbalanced play. This course teaches the Najdorf setup, the choice of Fischer and Kasparov.",
      sl: "Najbolj priljubljen — in najbolj bojevit — odgovor na 1.e4: namesto zrcaljenja črni zamenja krilnega kmeta za središčnega in dobi dinamično, neuravnoteženo igro. Ta tečaj uči najdorfovko, izbiro Fischerja in Kasparova.",
    },
    summary: {
      en: "The Sicilian trade — wing pawn for center pawn — buys you the half-open c-file and the better structure. The Najdorf adds …a6: no piece may land on b5, …e5 grabs the center, and the queenside pawns march. Play it against the computer and use that c-file!",
      sl: "Sicilijanska menjava — krilni kmet za središčnega — ti kupi polodprto linijo c in boljšo strukturo. Najdorfovka doda …a6: nobena figura ne sme na b5, …e5 zasede središče, kmetje na daminem krilu pa korakajo. Preizkusi jo proti računalniku in izkoristi linijo c!",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "e2e4",
            san: "e4",
            explain: {
              en: "White takes the center. The Sicilian won't mirror it — it fights back sideways.",
              sl: "Beli zasede središče. Sicilijanka ga ne zrcali — udari po strani.",
            },
          },
          {
            uci: "c7c5",
            san: "c5",
            hint: {
              en: "Strike at d4 from the side — with a wing pawn.",
              sl: "Od strani udari po polju d4 — s krilnim kmetom.",
            },
            explain: {
              en: "The Sicilian: the c-pawn controls d4, so White can't build the ideal e4+d4 center for free. If White forces d4 anyway, Black trades a wing pawn for a center pawn — a long-term structural win.",
              sl: "Sicilijanka: kmet c nadzoruje polje d4, zato beli ne more zastonj postaviti idealnega središča e4+d4. Če beli d4 vseeno izsili, črni zamenja krilnega kmeta za središčnega — dolgoročna strukturna pridobitev.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            explain: {
              en: "White develops and prepares d4 anyway.",
              sl: "Beli se razvija in vseeno pripravlja d4.",
            },
          },
          {
            uci: "d7d6",
            san: "d6",
            hint: {
              en: "A modest pawn move: control e5 and open the c8 bishop's future.",
              sl: "Skromna kmečka poteza: nadzoruj polje e5 in odpri prihodnost lovca c8.",
            },
            explain: {
              en: "…d6 guards e5 (no e4–e5 lunge) and supports the coming …e5 or …e6. The knight comes to f6 next, hitting e4.",
              sl: "…d6 varuje polje e5 (nič naskoka e4–e5) in podpira prihodnji …e5 ali …e6. Skakač pride zdaj na f6 in napade e4.",
            },
          },
          {
            uci: "d2d4",
            san: "d4",
            explain: {
              en: "White claims the center — but watch the trade that follows.",
              sl: "Beli zasede središče — a glej menjavo, ki sledi.",
            },
          },
          {
            uci: "c5d4",
            san: "cxd4",
            hint: {
              en: "Take it — trade your wing pawn for a center pawn.",
              sl: "Vzemi — zamenjaj krilnega kmeta za središčnega.",
            },
            explain: {
              en: "The Sicilian bargain: White gets open lines and quick development; Black keeps two center pawns against one and the half-open c-file for a lifetime of counterplay.",
              sl: "Sicilijanska kupčija: beli dobi odprte linije in hiter razvoj; črni obdrži dva središčna kmeta proti enemu in polodprto linijo c za trajno protiigro.",
            },
          },
          {
            uci: "f3d4",
            san: "Nxd4",
            explain: {
              en: "The knight recaptures and sits in the center — the Open Sicilian.",
              sl: "Skakač vzame nazaj in sedi v središču — odprta sicilijanka.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            hint: { en: "Develop with a threat on e4.", sl: "Razvij se z grožnjo kmetu na e4." },
            explain: {
              en: "Attack e4 immediately: White must spend a move defending it, so you develop for free.",
              sl: "Takoj napadi e4: beli mora porabiti potezo za njegovo obrambo, ti pa se razvijaš zastonj.",
            },
          },
          {
            uci: "b1c3",
            san: "Nc3",
            explain: {
              en: "Forced development: the knight defends e4.",
              sl: "Prisiljen razvoj: skakač brani e4.",
            },
          },
          {
            uci: "a7a6",
            san: "a6",
            hint: {
              en: "The Najdorf move: a tiny pawn step with a big idea.",
              sl: "Najdorfova poteza: majhen korak kmeta z veliko idejo.",
            },
            explain: {
              en: "The famous …a6: it takes b5 away from White's knights and bishop, prepares …e5 without check tricks on b5, and readies the …b5 expansion. A whole opening built on one quiet move.",
              sl: "Slavna …a6: belim skakačem in lovcu vzame polje b5, pripravi …e5 brez trikov s šahom na b5 in napoveduje širitev …b5. Cela otvoritev, zgrajena na eni tihi potezi.",
            },
          },
          {
            uci: "f1e2",
            san: "Be2",
            explain: {
              en: "The classical main line: White develops calmly and castles next.",
              sl: "Klasična glavna varianta: beli se mirno razvija in nato rokira.",
            },
          },
          {
            uci: "e7e5",
            san: "e5",
            hint: {
              en: "Now the center strike that …a6 prepared.",
              sl: "Zdaj udar po središču, ki ga je pripravila …a6.",
            },
            shapes: [{"orig":"e5","dest":"d4","brush":"paleGreen"}],
            explain: {
              en: "Kick the knight and grab space. The d5 hole is the price — but White can't reach it easily, and the bishop on e6, a knight on d7 and the …b5 push will cover it. Fischer's recipe.",
              sl: "Prežene skakača in vzame prostor. Cena je šibko polje d5 — a beli ga ne doseže zlahka; pokrijejo ga lovec na e6, skakač na d7 in potisk …b5. Fischerjev recept.",
            },
          },
          {
            uci: "d4b3",
            san: "Nb3",
            explain: {
              en: "The knight retreats — it was attacked, and d5 isn't available yet.",
              sl: "Skakač se umakne — bil je napaden, polje d5 pa še ni dosegljivo.",
            },
          },
          {
            uci: "f8e7",
            san: "Be7",
            hint: {
              en: "Develop the bishop and prepare to castle.",
              sl: "Razvij lovca in pripravi rokado.",
            },
            explain: {
              en: "Simple chess: bishop out, castle next. From e7 the bishop also watches the d6 pawn's back.",
              sl: "Preprost šah: lovec ven, sledi rokada. Z e7 lovec hkrati čuva hrbet kmeta na d6.",
            },
          },
          {
            uci: "e1g1",
            san: "O-O",
            explain: {
              en: "White castles. The position is a classic: White has space, Black has the better structure and clear plans.",
              sl: "Beli rokira. Položaj je klasičen: beli ima prostor, črni boljšo strukturo in jasne načrte.",
            },
          },
          {
            uci: "e8g8",
            san: "O-O",
            hint: {
              en: "King to safety — the opening is complete.",
              sl: "Kralj na varno — otvoritev je končana.",
            },
            explain: {
              en: "The Najdorf tabiya. From here Black develops the bishop to e6 and a knight to d7, pushes …b5, and plays on the queenside — while d5 stays guarded. You're not equalizing — you're playing for a win.",
              sl: "Najdorfova tabija. Od tod črni razvije lovca na e6 in skakača na d7, potisne …b5 in igra na daminem krilu — polje d5 pa ostaja pokrito. Ne igraš za izenačenje — igraš za zmago.",
            },
          },
        ],
      },
      {
        id: "no-nc6-9",
        at: 9,
        moves: [{ uci: "b8c6", san: "Nc6", reject: {
            en: "Developing the knight is the sound Classical Sicilian — but this course teaches the Najdorf: …a6 first, keeping every white piece off b5.",
            sl: "Razvoj skakača je zdrava klasična sicilijanka — a ta tečaj uči najdorfovko: najprej …a6, ki vsem belim figuram vzame polje b5.",
          } }],
      },
    ],
  },
  {
    slug: "london",
    group: "closed-flank",
    eco: "D02",
    name: { en: "The London System", sl: "Londonski sistem" },
    tagline: { en: "One setup against everything", sl: "Ena postavitev proti vsemu" },
    color: "white",
    intro: {
      en: "The modern club favorite: develop the dark-squared bishop *before* closing it in, build an unbreakable pawn pyramid, and play the same healthy plan against almost any Black setup.",
      sl: "Sodobni klubski ljubljenec: razvij črnopoljnega lovca, *preden* ga zapreš, zgradi nezlomljivo kmečko piramido in igraj isti zdravi načrt proti skoraj vsaki črni postavitvi.",
    },
    summary: {
      en: "The London recipe: bishop out to f4 first, pyramid c3–d4–e3, knights to d2 and f3, bishop to d3 — the same healthy setup against nearly everything. Then pick a plan: a knight to e5 with a kingside attack, or play on the c-file. Simple to learn, hard to beat.",
      sl: "Londonski recept: najprej lovec na f4, piramida c3–d4–e3, skakača na d2 in f3, lovec na d3 — ista zdrava postavitev proti skoraj vsemu. Nato izberi načrt: skakač na e5 z napadom na kraljevem krilu ali igra po liniji c. Preprosto za naučiti, težko za premagati.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "d2d4",
            san: "d4",
            hint: {
              en: "Start with the protected center pawn.",
              sl: "Začni z zavarovanim središčnim kmetom.",
            },
            explain: {
              en: "The queen's pawn opening: safe, solid, strategic.",
              sl: "Otvoritev daminega kmeta: varna, trdna, strateška.",
            },
          },
          {
            uci: "d7d5",
            san: "d5",
            explain: { en: "Black stakes the center symmetrically.", sl: "Črni simetrično zasede središče." },
          },
          {
            uci: "c1f4",
            san: "Bf4",
            hint: {
              en: "Develop the bishop that e3 would otherwise bury.",
              sl: "Razvij lovca, ki bi ga e3 sicer zakopal.",
            },
            shapes: [{"orig":"f4","dest":"b8","brush":"paleBlue"}],
            explain: {
              en: "The London move: the bishop takes the h2–b8 diagonal *before* e3 closes the door. Compare the Queen's Gambit Declined, where Black's c8 bishop stays buried for twenty moves.",
              sl: "Londonska poteza: lovec zasede diagonalo h2–b8, *preden* e3 zapre vrata. Primerjaj z zavrnjenim daminim gambitom, kjer črni lovec s c8 ostane zakopan dvajset potez.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            explain: { en: "Black develops naturally.", sl: "Črni se naravno razvija." },
          },
          {
            uci: "e2e3",
            san: "e3",
            hint: { en: "Open the f1 bishop and shore up d4.", sl: "Odpri lovca f1 in podpri d4." },
            explain: {
              en: "Now e3 costs nothing — the good bishop is already outside. The pawn supports d4 and opens the f1 bishop.",
              sl: "Zdaj e3 ne stane nič — dobri lovec je že zunaj. Kmet podpre d4 in odpre lovca f1.",
            },
          },
          {
            uci: "c7c5",
            san: "c5",
            explain: {
              en: "Black hits the base of your center — the standard counter.",
              sl: "Črni udari po dnu tvojega središča — običajni protiudar.",
            },
          },
          {
            uci: "c2c3",
            san: "c3",
            hint: {
              en: "Reinforce d4 and complete the pawn pyramid.",
              sl: "Podpri d4 in dokončaj kmečko piramido.",
            },
            explain: {
              en: "The London pyramid: c3–d4–e3. Every capture on d4 can be answered by a pawn recapture — the center simply cannot be knocked down.",
              sl: "Londonska piramida: c3–d4–e3. Vsako jemanje na d4 lahko vzameš nazaj s kmetom — središča preprosto ni mogoče podreti.",
            },
          },
          {
            uci: "b8c6",
            san: "Nc6",
            explain: {
              en: "More pressure on d4 — which is already overprotected.",
              sl: "Nov pritisk na d4 — ki pa je že večkrat zavarovan.",
            },
          },
          {
            uci: "b1d2",
            san: "Nd2",
            hint: {
              en: "Develop the queenside knight — it belongs on d2 here.",
              sl: "Razvij skakača z daminega krila — tu spada na d2.",
            },
            explain: {
              en: "On d2 the knight supports a later e4 and leaves c3 to the pawn. The g1 knight goes to f3 next.",
              sl: "Na d2 skakač podpira poznejši e4 in pušča polje c3 kmetu. Skakač z g1 gre zdaj na f3.",
            },
          },
          {
            uci: "e7e6",
            san: "e6",
            explain: {
              en: "Black opens the f8 bishop and solidifies d5.",
              sl: "Črni odpre lovca f8 in utrdi d5.",
            },
          },
          {
            uci: "g1f3",
            san: "Ngf3",
            hint: { en: "Finish the kingside development.", sl: "Dokončaj razvoj kraljevega krila." },
            explain: {
              en: "The full London setup is on the board: bishop f4, pawns c3–d4–e3, knights d2 and f3. You can reach this against nearly anything Black plays.",
              sl: "Polna londonska postavitev je na plošči: lovec f4, kmetje c3–d4–e3, skakača d2 in f3. Dosežeš jo proti skoraj vsemu, kar igra črni.",
            },
          },
          {
            uci: "f8d6",
            san: "Bd6",
            explain: {
              en: "Black challenges your best piece and offers a trade.",
              sl: "Črni izzove tvojo najboljšo figuro in ponudi menjavo.",
            },
          },
          {
            uci: "f4g3",
            san: "Bg3",
            hint: {
              en: "Don't trade the London bishop — tuck it away.",
              sl: "Ne menjaj londonskega lovca — umakni ga.",
            },
            explain: {
              en: "Keep the bishop: on g3 it still eyes b8, and if Black ever takes it, the h-file opens for your rook — a free attacking file.",
              sl: "Ohrani lovca: z g3 še vedno meri proti b8, in če ga črni kdaj vzame, se za tvojo trdnjavo odpre linija h — zastonj napadalna linija.",
            },
          },
          {
            uci: "e8g8",
            san: "O-O",
            explain: { en: "Black castles into the calm — for now.", sl: "Črni rokira v zatišje — za zdaj." },
          },
          {
            uci: "f1d3",
            san: "Bd3",
            hint: {
              en: "Point the second bishop at the black king.",
              sl: "Usmeri še drugega lovca proti črnemu kralju.",
            },
            explain: {
              en: "The attacking setup is complete: the d3 bishop eyes h7, a knight can land on e5, and h4–h5 plans follow. Castle either side — the London gives you the choice.",
              sl: "Napadalna postavitev je popolna: lovec z d3 meri na h7, skakač lahko skoči na e5, sledijo načrti s h4–h5. Rokiraš lahko na katerokoli stran — London ti pušča izbiro.",
            },
          },
        ],
      },

    ],
  },
  {
    slug: "french",
    group: "semi-open",
    eco: "C11",
    name: { en: "The French Defense", sl: "Francoska obramba" },
    tagline: { en: "A fortress with a counterpunch", sl: "Trdnjava s protiudarcem" },
    color: "black",
    intro: {
      en: "Solid but never passive: Black lets White build a proud pawn center, then attacks its base with …c5 until it collapses. This course teaches the Classical French — the pawn-chain battle in its purest form.",
      sl: "Trdna, a nikoli pasivna: črni pusti belemu postaviti ponosno kmečko središče, nato pa napada njegovo dno s …c5, dokler se ne sesede. Ta tečaj uči klasično francosko — boj kmečkih verig v najčistejši obliki.",
    },
    summary: {
      en: "The French formula: …e6 and …d5, meet the pawn chain with …c5 (and later …f6), pile pieces on the base at d4, and let White's proud center become a target. Learn to love the cramped first ten moves — the counterattack is worth it.",
      sl: "Francoska formula: …e6 in …d5, kmečko verigo pričakaj s …c5 (in pozneje …f6), figure zloži proti dnu na d4 in pusti, da ponosno belo središče postane tarča. Vzljubi utesnjenih prvih deset potez — protinapad je vreden tega.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "e2e4",
            san: "e4",
            explain: {
              en: "White grabs space. The French reply is a plan, not a mirror.",
              sl: "Beli vzame prostor. Francoski odgovor je načrt, ne zrcalo.",
            },
          },
          {
            uci: "e7e6",
            san: "e6",
            hint: { en: "Prepare …d5 the French way.", sl: "Pripravi …d5 po francosko." },
            explain: {
              en: "The French signature: …e6 supports tomorrow's …d5 and opens the f8 bishop. The cost is the c8 bishop's diagonal — the whole opening revolves around that trade-off.",
              sl: "Francoski podpis: …e6 podpre jutrišnji …d5 in odpre lovca f8. Cena je diagonala lovca c8 — vsa otvoritev se vrti okoli te menjave.",
            },
          },
          {
            uci: "d2d4",
            san: "d4",
            explain: { en: "White builds the full center.", sl: "Beli postavi polno središče." },
          },
          {
            uci: "d7d5",
            san: "d5",
            hint: { en: "Strike now, as prepared.", sl: "Udari zdaj, kot si pripravil." },
            explain: {
              en: "Challenge e4 at once. White must defend, push or trade — each choice defines a different French.",
              sl: "Takoj izzovi e4. Beli mora braniti, potisniti ali menjati — vsaka izbira določi drugačno francosko.",
            },
          },
          {
            uci: "b1c3",
            san: "Nc3",
            explain: { en: "The main line: a piece defends e4.", sl: "Glavna varianta: figura brani e4." },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            hint: { en: "Add a second attacker to e4.", sl: "Dodaj drugega napadalca na e4." },
            explain: {
              en: "Now e4 is attacked twice and defended once. White's best is the space-gaining push e5 — exactly what you want to bite on later.",
              sl: "Zdaj je e4 napaden dvakrat in branjen enkrat. Najboljše za belega je potisk e5, ki vzame prostor — točno tisto, v kar boš pozneje grizel.",
            },
          },
          {
            uci: "e4e5",
            san: "e5",
            explain: {
              en: "The pawn chain forms: d4–e5 against e6–d5. The chain's *base* is d4 — remember that square.",
              sl: "Nastane kmečka veriga: d4–e5 proti e6–d5. *Dno* verige je d4 — zapomni si to polje.",
            },
          },
          {
            uci: "f6d7",
            san: "Nfd7",
            hint: {
              en: "The attacked knight steps back — toward the …c5 break.",
              sl: "Napadeni skakač se umakne — proti udaru …c5.",
            },
            explain: {
              en: "Not to d5 (c4 would kick it again) but d7: from here the knight supports …c5 and can reroute via b6 or f8. French knights dance.",
              sl: "Ne na d5 (c4 bi ga spet pregnal), ampak na d7: od tod skakač podpira …c5 in se lahko preusmeri prek b6 ali f8. Francoski skakači plešejo.",
            },
          },
          {
            uci: "f2f4",
            san: "f4",
            explain: {
              en: "White reinforces e5 and grabs kingside space — the Steinitz setup.",
              sl: "Beli utrdi e5 in vzame prostor na kraljevem krilu — Steinitzeva postavitev.",
            },
          },
          {
            uci: "c7c5",
            san: "c5",
            hint: { en: "Attack the base of the chain.", sl: "Napadi dno verige." },
            shapes: [{"orig":"c5","dest":"d4","brush":"paleGreen"}],
            explain: {
              en: "The French counterpunch: hit d4, the chain's base. All of Black's play flows from this break — never delay it long.",
              sl: "Francoski protiudarec: udari po d4, dnu verige. Vsa črna igra izvira iz tega udara — nikoli ga ne odlašaj predolgo.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            explain: { en: "White defends d4 and develops.", sl: "Beli brani d4 in se razvija." },
          },
          {
            uci: "b8c6",
            san: "Nc6",
            hint: {
              en: "Pile on d4 with a developing move.",
              sl: "Z razvojno potezo dodaj pritisk na d4.",
            },
            explain: {
              en: "Development with a threat again: a second attacker joins against d4 — and the queen is coming next.",
              sl: "Spet razvoj z grožnjo: drugi napadalec se pridruži pritisku na d4 — sledi še dama.",
            },
          },
          {
            uci: "c1e3",
            san: "Be3",
            explain: {
              en: "White must defend d4 with pieces now.",
              sl: "Beli mora d4 zdaj braniti s figurami.",
            },
          },
          {
            uci: "d8b6",
            san: "Qb6",
            hint: {
              en: "Bring the queen where it hits d4 AND b2.",
              sl: "Postavi damo tja, kjer napada d4 IN b2.",
            },
            explain: {
              en: "The classic French move: the queen pressures d4 and pokes b2 at the same time. White's pieces are tied to defense — your position plays itself: …cxd4, the bishop to b4 with check, or the …f6 break next.",
              sl: "Klasična francoska poteza: dama pritiska na d4 in hkrati bode v b2. Bele figure so priklenjene na obrambo — tvoj položaj igra sam: sledi …cxd4, lovec na b4 s šahom ali udar …f6.",
            },
          },
        ],
      },
      {
        id: "no-c6-1",
        at: 1,
        moves: [{ uci: "c7c6", san: "c6", reject: {
            en: "That's the Caro-Kann — also excellent, and it has its own course! The French plays …e6: the d5 strike comes with the f8 bishop's support.",
            sl: "To je Caro-Kann — prav tako odlična, in ima svoj tečaj! Francoska igra …e6: udar …d5 pride s podporo lovca f8.",
          } }],
      },
    ],
  },
  {
    slug: "slav",
    group: "closed-flank",
    eco: "D18",
    name: { en: "The Slav Defense", sl: "Slovanska obramba" },
    tagline: {
      en: "Meet the Queen's Gambit without burying a bishop",
      sl: "Odgovor na damin gambit brez zakopanega lovca",
    },
    color: "black",
    intro: {
      en: "The other healthy answer to the Queen's Gambit: support d5 with …c6, keep the c8 bishop's diagonal open, and develop it to f5 *before* closing the door with …e6. As solid as the QGD — with the problem piece solved.",
      sl: "Drugi zdravi odgovor na damin gambit: podpri d5 s …c6, ohrani diagonalo lovca c8 odprto in ga razvij na f5, *preden* z …e6 zapreš vrata. Trdna kot zavrnjeni gambit — a z rešeno problemsko figuro.",
    },
    summary: {
      en: "The Slav formula: …d5 and …c6, take on c4 at the right moment, bishop out to f5 *before* …e6, then …Bb4 onto the square a4 weakened. Two active bishops and a granite structure — the Queen's Gambit answered on your terms.",
      sl: "Slovanska formula: …d5 in …c6, ob pravem trenutku vzemi na c4, lovec na f5, *preden* igraš …e6, nato lovec na b4 — na polje, ki ga je a4 oslabila. Dva aktivna lovca in granitna struktura — damin gambit, odgovorjen pod tvojimi pogoji.",
    },
    lines: [
      {
        id: "main",
        moves: [
          {
            uci: "d2d4",
            san: "d4",
            explain: {
              en: "The queen's pawn — White wants a calm, strategic fight.",
              sl: "Damin kmet — beli hoče miren, strateški boj.",
            },
          },
          {
            uci: "d7d5",
            san: "d5",
            hint: { en: "Claim your half of the center.", sl: "Zasedi svojo polovico središča." },
            explain: {
              en: "Symmetry for now — the character comes with the next two moves.",
              sl: "Za zdaj simetrija — značaj prineseta naslednji dve potezi.",
            },
          },
          {
            uci: "c2c4",
            san: "c4",
            explain: {
              en: "The Queen's Gambit: White offers a wing pawn to undermine d5.",
              sl: "Damin gambit: beli ponudi krilnega kmeta, da spodkoplje d5.",
            },
          },
          {
            uci: "c7c6",
            san: "c6",
            hint: {
              en: "Support d5 — with the pawn that doesn't block your bishop.",
              sl: "Podpri d5 — s kmetom, ki ne zapre tvojega lovca.",
            },
            explain: {
              en: "The Slav point: d5 is now rock-solid and the c8–h3 diagonal stays open. Caro-Kann players will feel right at home — it's the same idea against 1.d4.",
              sl: "Poanta slovanske: d5 je zdaj skalno trden, diagonala c8–h3 pa ostaja odprta. Igralci Caro-Kanna se boste počutili domače — ista ideja proti 1.d4.",
            },
          },
          {
            uci: "g1f3",
            san: "Nf3",
            explain: {
              en: "White develops; taking on d5 would only free your game.",
              sl: "Beli se razvija; jemanje na d5 bi tvojo igro le sprostilo.",
            },
          },
          {
            uci: "g8f6",
            san: "Nf6",
            hint: { en: "Develop toward the center.", sl: "Razvij se proti središču." },
            explain: {
              en: "Natural development. You *could* hold d5 forever — but the main line has a sharper idea next.",
              sl: "Naraven razvoj. D5 bi lahko držal večno — a glavna varianta ima zdaj ostrejšo zamisel.",
            },
          },
          {
            uci: "b1c3",
            san: "Nc3",
            explain: {
              en: "A second piece eyes d5. Now comes the Slav's committal moment.",
              sl: "Še druga figura gleda proti d5. Zdaj pride zavezujoči trenutek slovanske.",
            },
          },
          {
            uci: "d5c4",
            san: "dxc4",
            hint: {
              en: "Take the pawn — and make White work to win it back.",
              sl: "Vzemi kmeta — in prisili belega, da si ga prisluži nazaj.",
            },
            explain: {
              en: "The main line: give up the center *temporarily*. White will spend a move (a4) to regain the pawn — and that move weakens b4 forever. Meanwhile your bishop gets out.",
              sl: "Glavna varianta: središče prepusti *začasno*. Beli bo porabil potezo (a4), da dobi kmeta nazaj — in ta poteza za vedno oslabi polje b4. Medtem tvoj lovec pride ven.",
            },
          },
          {
            uci: "a2a4",
            san: "a4",
            explain: {
              en: "White stops …b5 (which would keep the extra pawn) — but weakens b4. Every move has a price.",
              sl: "Beli prepreči …b5 (ki bi obdržal kmeta viška) — a oslabi b4. Vsaka poteza ima ceno.",
            },
          },
          {
            uci: "c8f5",
            san: "Bf5",
            hint: {
              en: "The Slav dream: the bishop out *before* …e6.",
              sl: "Slovanske sanje: lovec ven, *preden* igraš …e6.",
            },
            shapes: [{"orig":"f5","dest":"b1","brush":"paleBlue"}],
            explain: {
              en: "Mission accomplished: the 'problem bishop' reaches its best diagonal before …e6 walls it in. This is the whole point of 1…d5 and 2…c6.",
              sl: "Naloga opravljena: 'problemski lovec' pride na najboljšo diagonalo, preden ga …e6 zazida. Prav to je vsa poanta potez …d5 in …c6.",
            },
          },
          {
            uci: "e2e3",
            san: "e3",
            explain: { en: "White finally moves on the c4 pawn.", sl: "Beli se končno loti kmeta na c4." },
          },
          {
            uci: "e7e6",
            san: "e6",
            hint: {
              en: "NOW close the door — the bishop is already outside.",
              sl: "ZDAJ zapri vrata — lovec je že zunaj.",
            },
            explain: {
              en: "…e6 costs nothing anymore: it feeds the f8 bishop and shores up the center. Compare the French, where this same move buries a bishop.",
              sl: "…e6 ne stane nič več: odpre lovca f8 in podpre središče. Primerjaj s francosko, kjer ista poteza lovca zakoplje.",
            },
          },
          {
            uci: "f1c4",
            san: "Bxc4",
            explain: {
              en: "White regains the pawn — material is level again, as promised.",
              sl: "Beli dobi kmeta nazaj — material je spet izenačen, kot obljubljeno.",
            },
          },
          {
            uci: "f8b4",
            san: "Bb4",
            hint: {
              en: "Develop with a pin — onto the weakened square.",
              sl: "Razvij se z vezjo — na oslabljeno polje.",
            },
            explain: {
              en: "Remember a4? It weakened b4, and your bishop lands there with tempo, pinning the knight. Both bishops active — Black has fully solved the opening.",
              sl: "Se spomniš poteze a4? Oslabila je polje b4 in tvoj lovec pristane tam s tempom ter veže skakača. Oba lovca aktivna — črni je otvoritev povsem rešil.",
            },
          },
          {
            uci: "e1g1",
            san: "O-O",
            explain: {
              en: "White castles and will try to build up with e4 or the queen to e2.",
              sl: "Beli rokira in bo poskušal graditi z e4 ali damo na e2.",
            },
          },
          {
            uci: "e8g8",
            san: "O-O",
            hint: {
              en: "Castle — the Slav setup is complete.",
              sl: "Rokiraj — slovanska postavitev je popolna.",
            },
            explain: {
              en: "The Dutch-variation tabiya: solid structure, active bishops, the b4 pin. Black follows with a knight to d7, the queen to e7 (or the …c5 break) and equal chances in a rich middlegame.",
              sl: "Tabija nizozemske variante: trdna struktura, aktivna lovca, vez na b4. Sledijo skakač na d7, dama na e7 (ali udar …c5) in enakopravne možnosti v bogati srednji igri.",
            },
          },
        ],
      },
      {
        id: "no-e6-3",
        at: 3,
        moves: [{ uci: "e7e6", san: "e6", reject: {
            en: "The Queen's Gambit Declined — perfectly sound, but it locks in the c8 bishop. The Slav keeps that diagonal open.",
            sl: "Zavrnjeni damin gambit — povsem zdrav, a zapre lovca na c8. Slovanska obramba to diagonalo ohrani odprto.",
          } }],
      },
    ],
  },
];

const L = (en: string, sl: string): L10n => ({ en, sl });
const E = (uci: string, san: string, en: string, sl: string): MoveSpec => ({
  uci,
  san,
  explain: L(en, sl),
});
const H = (
  uci: string,
  san: string,
  en: string,
  sl: string,
  hintEn: string,
  hintSl: string,
): MoveSpec => ({ uci, san, explain: L(en, sl), hint: L(hintEn, hintSl) });

function addLines(slug: string, lines: CourseLine[]): void {
  const course = OPENING_COURSES.find((candidate) => candidate.slug === slug);
  if (!course) throw new Error(`unknown opening course ${slug}`);
  course.lines.push(...lines);
}

// Each original course covers an early opponent choice and a sound learner
// fork. These are accepted variations; the converted `no-*` leaves above are
// instructive mistakes that still snap back for a retry.
addLines("italian", [
  {
    id: "two-knights",
    at: 5,
    name: L("Two Knights Defense", "Obramba dveh skakačev"),
    moves: [
      E("g8f6", "Nf6", "Black develops with tempo against e4.", "Črni razvije skakača s tempom proti e4."),
      E("d2d3", "d3", "White keeps the center compact and prepares to castle.", "Beli utrdi središče in pripravi rokado."),
      E("f8c5", "Bc5", "The bishop takes its active Italian diagonal.", "Lovec zasede aktivno italijansko diagonalo."),
      E("e1g1", "O-O", "White is safe and ready for c3 and d4.", "Beli je na varnem in pripravljen na c3 ter d4."),
    ],
  },
  {
    id: "evans-gambit",
    at: 6,
    name: L("Evans Gambit", "Evansov gambit"),
    moves: [
      E("b2b4", "b4", "The b-pawn offers itself to gain time and central space.", "Kmet b se ponudi za tempo in prostor v središču."),
      E("c5b4", "Bxb4", "Black accepts and asks White to prove the initiative.", "Črni sprejme in od belega zahteva dokaz pobude."),
      E("c2c3", "c3", "The bishop is chased while d4 is prepared.", "Lovec je napaden, hkrati se pripravlja d4."),
      E("b4a5", "Ba5", "The bishop preserves the diagonal.", "Lovec ohrani diagonalo."),
      E("d2d4", "d4", "White builds the classical Evans center with tempo.", "Beli s tempom zgradi klasično Evansovo središče."),
    ],
  },
]);

addLines("ruy-lopez", [
  {
    id: "berlin",
    at: 5,
    name: L("Berlin Defense", "Berlinska obramba"),
    moves: [
      E("g8f6", "Nf6", "Black counterattacks e4 instead of defending passively.", "Črni protinapade e4 namesto pasivne obrambe."),
      E("e1g1", "O-O", "White calmly castles and permits the central capture.", "Beli mirno rokira in dovoli jemanje v središču."),
      E("f6e4", "Nxe4", "Black takes the offered central pawn.", "Črni vzame ponujenega središčnega kmeta."),
      E("d2d4", "d4", "White opens lines and questions the knight.", "Beli odpre linije in izzove skakača."),
      E("e4d6", "Nd6", "The knight retreats into the famous Berlin structure.", "Skakač se umakne v znamenito berlinsko strukturo."),
    ],
  },
  {
    id: "exchange",
    at: 6,
    name: L("Exchange Variation", "Izmenjalna varianta"),
    moves: [
      E("b5c6", "Bxc6", "White gives up the bishop pair to damage Black's pawns.", "Beli odda par lovcev, da poškoduje črne kmete."),
      E("d7c6", "dxc6", "Black accepts doubled pawns but keeps the bishop pair.", "Črni sprejme podvojene kmete, a obdrži par lovcev."),
      E("d2d4", "d4", "White strikes before Black completes development.", "Beli udari, preden črni dokonča razvoj."),
      E("e5d4", "exd4", "The center opens.", "Središče se odpre."),
      E("d1d4", "Qxd4", "White recovers the pawn and enters a strategic ending.", "Beli dobi kmeta nazaj in preide v strateško končnico."),
    ],
  },
]);

addLines("queens-gambit", [
  {
    id: "accepted",
    at: 3,
    name: L("Queen's Gambit Accepted", "Sprejeti damin gambit"),
    moves: [
      E("d5c4", "dxc4", "Black accepts the pawn but gives up the center.", "Črni sprejme kmeta, vendar prepusti središče."),
      E("e2e3", "e3", "White opens the bishop's route to recover c4.", "Beli odpre lovcu pot do povrnitve kmeta c4."),
      E("e7e5", "e5", "Black uses the spare tempo to claim central space.", "Črni izkoristi tempo za prostor v središču."),
      E("f1c4", "Bxc4", "White regains the gambit pawn with development.", "Beli z razvojem dobi gambitnega kmeta nazaj."),
      E("e5d4", "exd4", "Black clarifies the center before White castles.", "Črni razjasni središče pred belo rokado."),
    ],
  },
  {
    id: "fianchetto-order",
    at: 4,
    name: L("Fianchetto move order", "Fianchetni vrstni red"),
    moves: [
      E("g1f3", "Nf3", "White keeps options open before committing the queen's knight.", "Beli ohrani možnosti, preden določi pot daminega skakača."),
      E("g8f6", "Nf6", "Black develops toward the key e4 square.", "Črni razvije skakača proti ključnemu polju e4."),
      E("g2g3", "g3", "White prepares Catalan pressure on the long diagonal.", "Beli pripravlja katalonski pritisk po dolgi diagonali."),
      E("f8e7", "Be7", "Black readies castling.", "Črni pripravlja rokado."),
      E("f1g2", "Bg2", "The bishop becomes a long-term queenside force.", "Lovec postane dolgoročna sila na daminem krilu."),
    ],
  },
]);

addLines("caro-kann", [
  {
    id: "advance",
    at: 4,
    name: L("Advance Variation", "Napredna varianta"),
    moves: [
      E("e4e5", "e5", "White closes the center and gains space.", "Beli zapre središče in pridobi prostor."),
      E("c8f5", "Bf5", "The Caro-Kann bishop gets outside the pawn chain.", "Caro-kannski lovec pride pred kmečko verigo."),
      E("g1f3", "Nf3", "White develops and supports the center.", "Beli razvija in podpira središče."),
      E("e7e6", "e6", "Black attacks the base of White's pawn chain next with …c5.", "Črni bo z …c5 napadel osnovo bele kmečke verige."),
      E("f1e2", "Be2", "White prepares to castle without overextending.", "Beli brez pretiravanja pripravi rokado."),
    ],
  },
  {
    id: "gurgenidze",
    at: 5,
    name: L("Gurgenidze System", "Gurgenidzejev sistem"),
    moves: [
      E("g7g6", "g6", "Black chooses a flexible kingside fianchetto.", "Črni izbere prožen kraljevi fianchetto."),
      E("h2h4", "h4", "White immediately claims kingside space.", "Beli takoj pridobi prostor na kraljevem krilu."),
      E("h7h5", "h5", "Black fixes the pawn before it can advance.", "Črni zadrži kmeta, preden ta napreduje."),
      E("g1f3", "Nf3", "White develops behind the space advantage.", "Beli se razvija za prostorsko prednostjo."),
      E("f8g7", "Bg7", "Black completes the characteristic setup.", "Črni dokonča značilno postavitev."),
    ],
  },
]);

addLines("sicilian", [
  {
    id: "moscow",
    at: 4,
    name: L("Moscow Variation", "Moskovska varianta"),
    moves: [
      E("f1b5", "Bb5+", "White checks before opening the center.", "Beli da šah pred odprtjem središča."),
      E("c8d7", "Bd7", "Black develops while meeting the check.", "Črni z razvojem odpravi šah."),
      E("b5d7", "Bxd7+", "White trades to reduce Black's dynamic options.", "Beli menja, da zmanjša črne dinamične možnosti."),
      E("d8d7", "Qxd7", "Black recaptures and keeps a sound Sicilian center.", "Črni vzame nazaj in ohrani zdravo sicilijansko središče."),
    ],
  },
  {
    id: "classical",
    at: 3,
    name: L("Classical Sicilian", "Klasična sicilijanka"),
    moves: [
      E("b8c6", "Nc6", "Black develops naturally and increases pressure on d4.", "Črni se naravno razvije in okrepi pritisk na d4."),
      E("d2d4", "d4", "White opens the position before Black consolidates.", "Beli odpre položaj, preden se črni utrdi."),
      E("c5d4", "cxd4", "The c-pawn trades for White's central d-pawn.", "Kmet c se zamenja za belega središčnega kmeta d."),
      E("f3d4", "Nxd4", "White restores a central piece.", "Beli postavi figuro v središče."),
      E("g8f6", "Nf6", "Black develops with pressure on e4.", "Črni se razvije s pritiskom na e4."),
      E("b1c3", "Nc3", "The Classical Sicilian tabiya is on the board.", "Na šahovnici je tabija klasične sicilijanke."),
    ],
  },
]);

addLines("london", [
  {
    id: "early-c5",
    at: 3,
    name: L("Early …c5 challenge", "Zgodnji izziv …c5"),
    moves: [
      E("c7c5", "c5", "Black attacks d4 before settling the kingside.", "Črni napade d4, še preden razvije kraljevo krilo."),
      E("e2e3", "e3", "White supports d4 and opens the light bishop.", "Beli podpre d4 in odpre belopoljnega lovca."),
      E("b8c6", "Nc6", "Black adds another attacker to d4.", "Črni doda še enega napadalca na d4."),
      E("c2c3", "c3", "The London triangle keeps the center firm.", "Londonski trikotnik utrdi središče."),
    ],
  },
  {
    id: "jobava",
    at: 4,
    name: L("Jobava London", "Jobavov London"),
    moves: [
      E("b1c3", "Nc3", "The Jobava setup develops aggressively instead of playing e3 first.", "Jobavova postavitev se razvija napadalno namesto zgodnjega e3."),
      E("e7e6", "e6", "Black supports d5 and opens the dark bishop.", "Črni podpre d5 in odpre črnopoljnega lovca."),
      E("c3b5", "Nb5", "The knight eyes c7 and creates concrete questions.", "Skakač meri na c7 in postavlja konkretna vprašanja."),
      E("b8a6", "Na6", "Black covers c7 without blocking the queen.", "Črni pokrije c7, ne da bi zaprl damo."),
    ],
  },
]);

addLines("french", [
  {
    id: "tarrasch",
    at: 4,
    name: L("Tarrasch Variation", "Tarrascheva varianta"),
    moves: [
      E("b1d2", "Nd2", "White supports e4 while avoiding a Winawer pin.", "Beli podpre e4 in se izogne Winawerjevi vezi."),
      E("g8f6", "Nf6", "Black immediately challenges e4.", "Črni takoj izzove e4."),
      E("e4e5", "e5", "White gains space and attacks the knight.", "Beli pridobi prostor in napade skakača."),
      E("f6d7", "Nfd7", "Black reroutes and prepares the thematic …c5 break.", "Črni preusmeri skakača in pripravi značilni udar …c5."),
    ],
  },
  {
    id: "winawer",
    at: 5,
    name: L("Winawer Variation", "Winawerjeva varianta"),
    moves: [
      E("f8b4", "Bb4", "Black pins the knight and intensifies the fight for e4.", "Črni zveže skakača in zaostri boj za e4."),
      E("e4e5", "e5", "White closes the center and gains space.", "Beli zapre središče in pridobi prostor."),
      E("c7c5", "c5", "Black attacks the base of the pawn chain.", "Črni napade osnovo kmečke verige."),
      E("a2a3", "a3", "White asks the bishop to decide.", "Beli prisili lovca k odločitvi."),
      E("b4c3", "Bxc3+", "Black damages White's queenside structure.", "Črni poškoduje belo strukturo na daminem krilu."),
      E("b2c3", "bxc3", "White accepts doubled pawns for the bishop pair and space.", "Beli sprejme podvojene kmete za par lovcev in prostor."),
    ],
  },
]);

addLines("slav", [
  {
    id: "exchange",
    at: 4,
    name: L("Exchange Slav", "Izmenjalna slovanka"),
    moves: [
      E("c4d5", "cxd5", "White releases the central tension early.", "Beli zgodaj sprosti napetost v središču."),
      E("c6d5", "cxd5", "Black restores symmetry without locking in the bishop.", "Črni obnovi simetrijo, ne da bi zaprl lovca."),
      E("b1c3", "Nc3", "White develops toward d5.", "Beli razvije skakača proti d5."),
      E("g8f6", "Nf6", "Black mirrors and controls e4.", "Črni zrcali in nadzira e4."),
      E("c1f4", "Bf4", "White develops outside the pawn chain.", "Beli razvije lovca pred kmečko verigo."),
    ],
  },
  {
    id: "semi-slav",
    at: 7,
    name: L("Semi-Slav", "Polslovanska obramba"),
    moves: [
      E("e7e6", "e6", "Black reinforces d5, accepting a temporarily closed bishop.", "Črni utrdi d5 in začasno zapre lovca."),
      E("e2e3", "e3", "White supports the center and opens the f1 bishop.", "Beli podpre središče in odpre lovca f1."),
      E("b8d7", "Nbd7", "Black develops behind the compact pawn triangle.", "Črni se razvije za kompaktnim kmečkim trikotnikom."),
      E("f1d3", "Bd3", "White points at the kingside.", "Beli usmeri lovca proti kraljevemu krilu."),
      E("d5c4", "dxc4", "Black releases the tension at a convenient moment.", "Črni v primernem trenutku sprosti napetost."),
      E("d3c4", "Bxc4", "White recaptures with an active bishop.", "Beli vzame nazaj z aktivnim lovcem."),
    ],
  },
]);

OPENING_COURSES.push({
  slug: "scandinavian",
  group: "semi-open",
  eco: "B01",
  name: L("Scandinavian Defense", "Skandinavska obramba"),
  tagline: L("Challenge 1.e4 immediately", "Takoj izzovi 1.e4"),
  color: "black",
  intro: L(
    "The Scandinavian asks White's center a direct question on move one. Learn a sound queen setup, the Modern alternative, and the main early deviations from both sides.",
    "Skandinavska obramba belemu središču že s prvo potezo postavi neposredno vprašanje. Spoznaj zdravo postavitev z damo, moderno alternativo in glavna zgodnja odstopanja obeh strani.",
  ),
  summary: L(
    "Meet 1.e4 with …d5, develop the queen without losing the thread, then finish development with …Nf6, …c6, …Bf5 and …e6. You now recognize the main tabiya, the Modern line, and both declined systems.",
    "Na 1.e4 odgovori z …d5, razvij damo brez izgube niti ter dokončaj razvoj z …Nf6, …c6, …Bf5 in …e6. Zdaj poznaš glavno tabijo, moderno varianto in oba sistema z zavrnitvijo menjave.",
  ),
  lines: [
    {
      id: "main",
      moves: [
        E("e2e4", "e4", "White takes classical central space.", "Beli klasično zasede središče."),
        H("d7d5", "d5", "Black challenges e4 before White can reinforce it.", "Črni izzove e4, preden ga beli utrdi.", "Strike at the e4 pawn immediately.", "Takoj udari po kmetu e4."),
        E("e4d5", "exd5", "White accepts the central exchange.", "Beli sprejme menjavo v središču."),
        H("d8d5", "Qxd5", "The queen recaptures; it will move once more without conceding the center.", "Dama vzame nazaj; premaknila se bo še enkrat, ne da bi prepustila središče.", "Recapture the pawn with the queen.", "Vzemi kmeta nazaj z damo."),
        E("b1c3", "Nc3", "White develops with tempo against the queen.", "Beli se razvije s tempom proti dami."),
        H("d5a5", "Qa5", "The queen keeps useful pressure from a5 and avoids another immediate chase.", "Dama z a5 ohrani koristen pritisk in se izogne takojšnjemu preganjanju.", "Keep the queen active on the a5–e1 diagonal.", "Ohrani damo aktivno na diagonali a5–e1."),
        E("d2d4", "d4", "White builds the broad center.", "Beli zgradi široko središče."),
        H("g8f6", "Nf6", "Black develops and pressures d5 and e4.", "Črni razvije skakača in pritisne na d5 ter e4.", "Develop the king knight toward the center.", "Razvij kraljevega skakača proti središču."),
        E("g1f3", "Nf3", "White develops and prepares castling.", "Beli se razvije in pripravi rokado."),
        H("c7c6", "c6", "The c-pawn builds a retreat square and supports …Bf5.", "Kmet c pripravi umik in podpira …Bf5.", "Build the compact Scandinavian pawn shell.", "Zgradi kompaktno skandinavsko kmečko lupino."),
        E("f1c4", "Bc4", "White points the bishop toward f7.", "Beli usmeri lovca proti f7."),
        H("c8f5", "Bf5", "The light bishop develops before …e6 closes its diagonal.", "Belopoljni lovec se razvije, preden …e6 zapre njegovo diagonalo.", "Develop the c8 bishop before playing …e6.", "Razvij lovca c8 pred potezo …e6."),
        E("c1d2", "Bd2", "White connects pieces and may castle long.", "Beli poveže figure in lahko rokira dolgo."),
        H("e7e6", "e6", "Black completes the sturdy Caro-Kann-like structure.", "Črni dokonča trdno strukturo, podobno Caro-Kannu.", "Now support d5 and open the dark bishop.", "Zdaj podpri d5 in odpri črnopoljnega lovca."),
        E("d1e2", "Qe2", "White prepares central play and long castling.", "Beli pripravlja igro v središču in dolgo rokado."),
        H("f8b4", "Bb4", "The bishop pins the knight in a real Scandinavian tabiya.", "Lovec zveže skakača v pravi skandinavski tabiji.", "Develop with a pin on c3.", "Razvij se z vezjo na c3."),
      ],
    },
    {
      id: "declined-nc3",
      at: 2,
      name: L("Scandinavian Declined", "Zavrnjena skandinavska"),
      moves: [
        E("b1c3", "Nc3", "White protects e4 and declines the exchange.", "Beli zaščiti e4 in zavrne menjavo."),
        E("d5d4", "d4", "Black gains space and dislodges the knight.", "Črni pridobi prostor in prežene skakača."),
        E("c3e2", "Nce2", "The c-knight reroutes without blocking the c-pawn forever.", "Skakač c se preusmeri, ne da bi trajno zaprl kmeta c."),
        E("e7e5", "e5", "Black reinforces the advanced pawn and claims the center.", "Črni podpre napredovanega kmeta in zasede središče."),
      ],
    },
    {
      id: "declined-advance",
      at: 2,
      name: L("Advance Variation", "Napredna varianta"),
      moves: [
        E("e4e5", "e5", "White gains space instead of exchanging.", "Beli pridobi prostor namesto menjave."),
        E("c8f5", "Bf5", "Black develops outside the pawn chain.", "Črni razvije lovca pred kmečko verigo."),
        E("d2d4", "d4", "White supports the advanced pawn.", "Beli podpre napredovanega kmeta."),
        E("e7e6", "e6", "Black prepares …c5 against the pawn chain.", "Črni pripravi …c5 proti kmečki verigi."),
      ],
    },
    {
      id: "modern-portuguese",
      at: 3,
      name: L("Modern / Portuguese", "Moderna / portugalska varianta"),
      moves: [
        E("g8f6", "Nf6", "Black develops instead of recapturing immediately.", "Črni se razvije namesto takojšnjega jemanja nazaj."),
        E("d2d4", "d4", "White holds the extra pawn with a broad center.", "Beli zadrži dodatnega kmeta s širokim središčem."),
        E("f6d5", "Nxd5", "Black recovers the pawn with a developed piece.", "Črni dobi kmeta nazaj z razvito figuro."),
        E("c2c4", "c4", "White gains space and attacks the knight.", "Beli pridobi prostor in napade skakača."),
        E("d5b6", "Nb6", "The knight keeps useful queenside squares.", "Skakač ohrani koristna polja na daminem krilu."),
      ],
    },
    {
      id: "no-scandinavian-gambit",
      at: 3,
      moves: [{
        uci: "c7c6",
        san: "c6",
        reject: L("That is the Scandinavian Gambit, a different course; use …Nf6 here for the covered Modern line.", "To je skandinavski gambit, ki sodi v drug tečaj; za obravnavano moderno varianto tukaj igraj …Nf6."),
      }],
    },
    {
      id: "tiviakov",
      at: 5,
      name: L("Tiviakov setup", "Tiviakovova postavitev"),
      moves: [
        E("d5d6", "Qd6", "The queen supports the center from a less exposed square.", "Dama podpira središče z manj izpostavljenega polja."),
        E("d2d4", "d4", "White builds the center.", "Beli gradi središče."),
        E("g8f6", "Nf6", "Black develops naturally.", "Črni se naravno razvije."),
        E("g1f3", "Nf3", "White prepares castling.", "Beli pripravi rokado."),
        E("a7a6", "a6", "The Tiviakov setup restrains a queenside jump and prepares …b5.", "Tiviakovova postavitev omeji skok na daminem krilu in pripravi …b5."),
      ],
    },
    {
      id: "bankers",
      at: 5,
      name: L("Banker's Variation", "Bankirska varianta"),
      moves: [
        E("d5d8", "Qd8", "The queen returns home, conceding a tempo for complete safety.", "Dama se vrne domov in za popolno varnost prepusti tempo."),
        E("d2d4", "d4", "White takes space.", "Beli pridobi prostor."),
        E("g8f6", "Nf6", "Black resumes normal development.", "Črni nadaljuje normalen razvoj."),
        E("g1f3", "Nf3", "White brings out the king knight.", "Beli razvije kraljevega skakača."),
        E("g7g6", "g6", "Black chooses a compact fianchetto structure.", "Črni izbere kompaktno fianchetno strukturo."),
      ],
    },
    {
      id: "no-qe5",
      at: 5,
      moves: [{
        uci: "d5e5",
        san: "Qe5+",
        reject: L("The check looks active, but the queen keeps getting hit; choose a5, d6, or d8.", "Šah deluje aktivno, vendar bodo damo še naprej preganjali; izberi a5, d6 ali d8."),
      }],
    },
  ],
});

// Current young-elite repertoires repeatedly feature the Catalan, Nimzo-Indian,
// and flexible Réti/English move orders. They form a dedicated UI section while
// remaining complete two-sided courses like every other entry.
OPENING_COURSES.push(
  {
    slug: "catalan",
    group: "closed-flank",
    eco: "E05",
    name: L("Catalan Opening", "Katalonska otvoritev"),
    tagline: L("Long-diagonal pressure used by today's elite", "Pritisk po dolgi diagonali za današnjo elito"),
    color: "white",
    intro: L("The Catalan combines a Queen's Gambit center with a kingside fianchetto. White often trades a pawn for lasting pressure on the long diagonal.", "Katalonska otvoritev združi središče daminega gambita s kraljevim fianchettom. Beli pogosto zamenja kmeta za trajen pritisk po dolgi diagonali."),
    summary: L("Build d4+c4, fianchetto the bishop, castle, and pressure the queenside rather than forcing tactics. You can now meet Open, Closed, and flexible move orders.", "Zgradi d4+c4, postavi lovca v fianchetto, rokiraj in pritiskaj na damino krilo namesto siljenja taktike. Zdaj znaš igrati odprte, zaprte in prožne vrstne rede potez."),
    lines: [
      {
        id: "main",
        moves: [
          H("d2d4", "d4", "White claims central space.", "Beli zasede središče.", "Start with the queen pawn.", "Začni z daminim kmetom."),
          E("g8f6", "Nf6", "Black keeps the central structure flexible.", "Črni ohrani središčno strukturo prožno."),
          H("c2c4", "c4", "White challenges d5 before it appears.", "Beli izzove d5, še preden se pojavi.", "Add the c-pawn to your central claim.", "Dodaj kmeta c svojemu središčnemu nadzoru."),
          E("e7e6", "e6", "Black opens the dark bishop and prepares …d5.", "Črni odpre črnopoljnega lovca in pripravi …d5."),
          H("g2g3", "g3", "The Catalan fianchetto begins.", "Začne se katalonski fianchetto.", "Prepare the long-diagonal bishop.", "Pripravi lovca za dolgo diagonalo."),
          E("d7d5", "d5", "Black establishes the classical center.", "Črni vzpostavi klasično središče."),
          H("f1g2", "Bg2", "The signature bishop targets the queenside.", "Značilni lovec meri na damino krilo.", "Place the bishop on the long diagonal.", "Postavi lovca na dolgo diagonalo."),
          E("f8e7", "Be7", "Black prepares a safe castle.", "Črni pripravi varno rokado."),
          H("g1f3", "Nf3", "White develops without blocking the bishop.", "Beli se razvije, ne da bi zaprl lovca.", "Develop the king knight.", "Razvij kraljevega skakača."),
          E("e8g8", "O-O", "Black secures the king.", "Črni umakne kralja na varno."),
          H("e1g1", "O-O", "White completes the fianchetto setup.", "Beli dokonča fianchetno postavitev.", "Castle and connect your rook to the center.", "Rokiraj in poveži trdnjavo s središčem."),
          E("d5c4", "dxc4", "Black opens the Catalan and temporarily wins a pawn.", "Črni odpre katalonsko igro in začasno osvoji kmeta."),
          H("d1c2", "Qc2", "The queen eyes c4 and h7 at once.", "Dama hkrati meri na c4 in h7.", "Attack the c4 pawn with the queen.", "Napadi kmeta c4 z damo."),
          E("a7a6", "a6", "Black prepares to hold the extra pawn with …b5.", "Črni z …b5 pripravlja obrambo dodatnega kmeta."),
          H("c2c4", "Qxc4", "White restores material while preserving diagonal pressure.", "Beli izenači material in ohrani pritisk po diagonali.", "Recover the pawn with your queen.", "Vzemi kmeta nazaj z damo."),
        ],
      },
      {
        id: "open-catalan",
        at: 7,
        name: L("Open Catalan", "Odprta katalonska"),
        moves: [
          E("d5c4", "dxc4", "Black opens the diagonal and tries to keep the pawn.", "Črni odpre diagonalo in poskuša obdržati kmeta."),
          E("g1f3", "Nf3", "White values development over an immediate pawn recovery.", "Beli ceni razvoj bolj kot takojšnjo povrnitev kmeta."),
          E("a7a6", "a6", "Black prepares …b5.", "Črni pripravi …b5."),
          E("e1g1", "O-O", "White castles before attacking the queenside.", "Beli rokira pred napadom na damino krilo."),
          E("b7b5", "b5", "Black supports the extra pawn.", "Črni podpre dodatnega kmeta."),
          E("a2a4", "a4", "White undermines the pawn chain immediately.", "Beli takoj spodkoplje kmečko verigo."),
        ],
      },
      {
        id: "closed-catalan",
        at: 7,
        name: L("Closed Catalan", "Zaprta katalonska"),
        moves: [
          E("c7c6", "c6", "Black reinforces d5 and blunts the long diagonal.", "Črni utrdi d5 in oslabi dolgo diagonalo."),
          E("g1f3", "Nf3", "White develops patiently.", "Beli se potrpežljivo razvija."),
          E("b8d7", "Nbd7", "Black supports the compact center.", "Črni podpre kompaktno središče."),
          E("e1g1", "O-O", "White castles into a long positional battle.", "Beli rokira v dolg pozicijski boj."),
        ],
      },
      {
        id: "flexible-order",
        at: 6,
        name: L("Flexible move order", "Prožen vrstni red"),
        moves: [
          E("g1f3", "Nf3", "White delays the bishop while preserving the same setup.", "Beli odloži razvoj lovca in ohrani isto postavitev."),
          E("f8e7", "Be7", "Black prepares to castle.", "Črni pripravi rokado."),
          E("f1g2", "Bg2", "White completes the transposition.", "Beli dokonča prehod v isti položaj."),
          E("e8g8", "O-O", "Black secures the king.", "Črni umakne kralja na varno."),
          E("e1g1", "O-O", "The familiar Catalan position appears by another route.", "Znani katalonski položaj nastane po drugi poti."),
        ],
      },
    ],
  },
  {
    slug: "nimzo-indian",
    group: "closed-flank",
    eco: "E32",
    name: L("Nimzo-Indian Defense", "Nimco-indijska obramba"),
    tagline: L("Pin, pressure, and structural choices", "Vezi, pritisk in strukturne odločitve"),
    color: "black",
    intro: L("The Nimzo-Indian prevents e4 by pinning the c3 knight. Black willingly trades bishop for knight when the resulting structure or dark-square control justifies it.", "Nimco-indijska prepreči e4 z vezjo skakača c3. Črni rad zamenja lovca za skakača, ko to upravičita nastala struktura ali nadzor črnih polj."),
    summary: L("Develop with …Nf6 and …e6, pin Nc3 with …Bb4, then choose between castling, …d5, and …c5 according to White's setup. You now cover Rubinstein, Sämisch, Qc2, and Hübner structures.", "Razvij se z …Nf6 in …e6, zveži Nc3 z …Bb4, nato glede na belo postavitev izberi rokado, …d5 ali …c5. Zdaj poznaš Rubinsteinovo, Sämischevo, Qc2 in Hübnerjevo strukturo."),
    lines: [
      {
        id: "main",
        moves: [
          E("d2d4", "d4", "White takes queen-pawn space.", "Beli z daminim kmetom zasede prostor."),
          H("g8f6", "Nf6", "Black controls e4 without fixing the center.", "Črni nadzira e4 brez določitve središča.", "Develop and stop an easy e4.", "Razvij se in prepreči lahek e4."),
          E("c2c4", "c4", "White expands the central claim.", "Beli razširi središčni nadzor."),
          H("e7e6", "e6", "Black opens the bishop and keeps several defenses available.", "Črni odpre lovca in ohrani več obramb.", "Open the f8 bishop and prepare the pin.", "Odpri lovca f8 in pripravi vez."),
          E("b1c3", "Nc3", "White supports e4 but exposes the knight to a pin.", "Beli podpre e4, vendar izpostavi skakača vezi."),
          H("f8b4", "Bb4", "The Nimzo pin fights for e4 through the knight.", "Nimco-indijska vez se prek skakača bori za e4.", "Pin the c3 knight to the king.", "Zveži skakača c3 s kraljem."),
          E("e2e3", "e3", "The Rubinstein system builds a solid center.", "Rubinsteinov sistem gradi trdno središče."),
          H("e8g8", "O-O", "Black castles before clarifying the center.", "Črni rokira pred razjasnitvijo središča.", "Secure the king while keeping options open.", "Zavaruj kralja in ohrani možnosti."),
          E("f1d3", "Bd3", "White aims toward h7.", "Beli meri proti h7."),
          H("d7d5", "d5", "Black establishes a classical central foothold.", "Črni vzpostavi klasično oporo v središču.", "Challenge White's center with …d5.", "Izzovi belo središče z …d5."),
          E("g1f3", "Nf3", "White completes natural development.", "Beli dokonča naraven razvoj."),
          H("c7c5", "c5", "Black attacks d4 and creates dynamic tension.", "Črni napade d4 in ustvari dinamično napetost.", "Add pressure to d4 with the c-pawn.", "Dodaj pritisk na d4 s kmetom c."),
          E("e1g1", "O-O", "White gets the king safe.", "Beli umakne kralja na varno."),
          H("b8c6", "Nc6", "Black completes a rich Nimzo tabiya.", "Črni dokonča bogato nimco-indijsko tabijo.", "Develop the last queenside minor piece.", "Razvij zadnjo lahko figuro na daminem krilu."),
        ],
      },
      {
        id: "qc2",
        at: 6,
        name: L("Classical 4.Qc2", "Klasična 4.Qc2"),
        moves: [
          E("d1c2", "Qc2", "White protects c3 and supports e4 without doubling pawns.", "Beli zaščiti c3 in podpre e4 brez podvojenih kmetov."),
          E("e8g8", "O-O", "Black calmly castles.", "Črni mirno rokira."),
          E("e2e4", "e4", "White builds the ideal center.", "Beli zgradi idealno središče."),
          E("d7d5", "d5", "Black strikes before the center settles.", "Črni udari, preden se središče utrdi."),
          E("e4e5", "e5", "White gains space and attacks the knight.", "Beli pridobi prostor in napade skakača."),
          E("f6d7", "Nfd7", "Black reroutes toward the central breaks.", "Črni preusmeri skakača proti središčnim udarom."),
        ],
      },
      {
        id: "saemisch",
        at: 6,
        name: L("Sämisch Variation", "Sämischeva varianta"),
        moves: [
          E("a2a3", "a3", "White immediately questions the bishop.", "Beli takoj izzove lovca."),
          E("b4c3", "Bxc3+", "Black accepts the structural bargain.", "Črni sprejme strukturno kupčijo."),
          E("b2c3", "bxc3", "White gets the bishop pair and a broad center.", "Beli dobi par lovcev in široko središče."),
          E("d7d5", "d5", "Black attacks the center before it advances.", "Črni napade središče, preden napreduje."),
          E("c4d5", "cxd5", "White releases the tension.", "Beli sprosti napetost."),
          E("e6d5", "exd5", "Black obtains active central play against the doubled pawns.", "Črni dobi aktivno središčno igro proti podvojenim kmetom."),
        ],
      },
      {
        id: "huebner",
        at: 7,
        name: L("Hübner structure", "Hübnerjeva struktura"),
        moves: [
          E("c7c5", "c5", "Black attacks d4 immediately.", "Črni takoj napade d4."),
          E("f1d3", "Bd3", "White develops toward the kingside.", "Beli se razvije proti kraljevemu krilu."),
          E("b8c6", "Nc6", "Black increases central pressure.", "Črni okrepi pritisk v središču."),
          E("g1f3", "Nf3", "White reinforces d4 and e5.", "Beli okrepi d4 in e5."),
          E("b4c3", "Bxc3+", "Black gives up the bishop to define the structure.", "Črni odda lovca, da določi strukturo."),
          E("b2c3", "bxc3", "White's bishop pair is balanced by targets on c3 and c4.", "Beli par lovcev uravnotežita tarči na c3 in c4."),
        ],
      },
    ],
  },
  {
    slug: "reti-english",
    group: "closed-flank",
    eco: "A09",
    name: L("Réti–English Complex", "Rétijev–angleški kompleks"),
    tagline: L("Flexible move orders before a central strike", "Prožni vrstni redi pred udarom v središču"),
    color: "white",
    intro: L("Modern elite repertoires often delay the central pawns. Nf3, g3, and Bg2 reveal little, invite several black setups, and can transpose into an English or Réti position.", "Sodobni elitni repertoarji pogosto odložijo poteze s središčnimi kmeti. Nf3, g3 in Bg2 razkrijejo malo, dovolijo več črnih postavitev ter lahko preidejo v angleški ali Rétijev položaj."),
    summary: L("Develop flexibly with Nf3, g3, and Bg2, then choose d3+e4 or a c4 English structure. You can now recognize classical, kingside-fianchetto, and symmetrical replies.", "Razvij se prožno z Nf3, g3 in Bg2, nato izberi d3+e4 ali angleško strukturo s c4. Zdaj prepoznaš klasične, kraljevo-fianchetne in simetrične odgovore."),
    lines: [
      {
        id: "main",
        moves: [
          H("g1f3", "Nf3", "White develops while keeping every central pawn flexible.", "Beli se razvije in ohrani vse središčne kmete prožne.", "Develop without declaring your center.", "Razvij se, ne da bi razkril središče."),
          E("d7d5", "d5", "Black takes classical central space.", "Črni klasično zasede središče."),
          H("g2g3", "g3", "White prepares the long-diagonal bishop.", "Beli pripravi lovca za dolgo diagonalo.", "Prepare a kingside fianchetto.", "Pripravi kraljevi fianchetto."),
          E("g8f6", "Nf6", "Black develops and supports the center.", "Črni se razvije in podpre središče."),
          H("f1g2", "Bg2", "The bishop pressures d5 from afar.", "Lovec od daleč pritiska na d5.", "Complete the fianchetto.", "Dokončaj fianchetto."),
          E("e7e6", "e6", "Black builds a solid classical shell.", "Črni zgradi trdno klasično lupino."),
          H("e1g1", "O-O", "White secures the king before choosing the center.", "Beli zavaruje kralja pred izbiro središčne postavitve.", "Castle before expanding.", "Rokiraj pred širitvijo."),
          E("f8e7", "Be7", "Black also prepares to castle.", "Tudi črni pripravi rokado."),
          H("d2d3", "d3", "White supports an eventual e4 break.", "Beli podpre prihodnji udar e4.", "Prepare e4 with the d-pawn.", "Pripravi e4 s kmetom d."),
          E("e8g8", "O-O", "Both kings are safe.", "Oba kralja sta na varnem."),
          H("b1d2", "Nbd2", "The knight supports e4 without blocking the c-pawn.", "Skakač podpre e4, ne da bi zaprl kmeta c.", "Develop the queen knight toward e4.", "Razvij daminega skakača proti e4."),
          E("c7c5", "c5", "Black challenges the light squares.", "Črni izzove bela polja."),
          H("e2e4", "e4", "White finally strikes in the center.", "Beli končno udari v središču.", "Use your preparation to play e4.", "Izkoristi pripravo in igraj e4."),
          E("b8c6", "Nc6", "Black develops into a rich reversed structure.", "Črni se razvije v bogato obrnjeno strukturo."),
        ],
      },
      {
        id: "kingside-fianchetto",
        at: 1,
        name: L("King's Indian setup", "Kraljevo-indijska postavitev"),
        moves: [
          E("g8f6", "Nf6", "Black mirrors the flexible development.", "Črni zrcali prožen razvoj."),
          E("g2g3", "g3", "White prepares the bishop.", "Beli pripravi lovca."),
          E("g7g6", "g6", "Black chooses a matching fianchetto.", "Črni izbere zrcalni fianchetto."),
          E("f1g2", "Bg2", "White claims the long diagonal.", "Beli zasede dolgo diagonalo."),
          E("f8g7", "Bg7", "Black does the same.", "Črni stori enako."),
          E("e1g1", "O-O", "White castles.", "Beli rokira."),
          E("e8g8", "O-O", "The center remains fluid for both sides.", "Središče ostane prožno za oba."),
        ],
      },
      {
        id: "english-four-knights",
        at: 0,
        name: L("English Four Knights", "Angleška s štirimi skakači"),
        moves: [
          E("c2c4", "c4", "White starts with queenside pressure instead of Nf3.", "Beli začne s pritiskom na daminem krilu namesto Nf3."),
          E("e7e5", "e5", "Black creates a reversed Sicilian center.", "Črni ustvari obrnjeno sicilijansko središče."),
          E("b1c3", "Nc3", "White increases control of d5.", "Beli okrepi nadzor nad d5."),
          E("g8f6", "Nf6", "Black develops toward d5 and e4.", "Črni se razvije proti d5 in e4."),
          E("g2g3", "g3", "White prepares the English fianchetto.", "Beli pripravi angleški fianchetto."),
          E("d7d5", "d5", "Black occupies the center before White completes development.", "Črni zasede središče, preden beli dokonča razvoj."),
        ],
      },
      {
        id: "symmetrical-english",
        at: 1,
        name: L("Symmetrical English", "Simetrična angleška"),
        moves: [
          E("c7c5", "c5", "Black controls d4 from the flank.", "Črni s krila nadzira d4."),
          E("c2c4", "c4", "White enters a symmetrical English structure.", "Beli preide v simetrično angleško strukturo."),
          E("b8c6", "Nc6", "Black develops toward the center.", "Črni se razvije proti središču."),
          E("g2g3", "g3", "White prepares the bishop.", "Beli pripravi lovca."),
          E("g7g6", "g6", "Black mirrors the fianchetto.", "Črni zrcali fianchetto."),
          E("f1g2", "Bg2", "White completes development on the long diagonal.", "Beli dokonča razvoj na dolgi diagonali."),
          E("f8g7", "Bg7", "A flexible symmetrical tabiya is reached.", "Nastane prožna simetrična tabija."),
        ],
      },
    ],
  },
);

// Keep the richly explained courses first inside each catalog section. The
// supported tier broadens the repertoire without pretending generic copy is
// equivalent to reviewed move-by-move coaching.
OPENING_COURSES.push(...SUPPORTED_OPENING_COURSES);
